import { QAChecker, type QAResult } from "@/components/cat/qaChecker";
import GoToSegmentDialog from "@/components/cat/GoToSegmentDialog";
import KeyboardShortcutsDialog from "@/components/cat/KeyboardShortcutsDialog";
import QAIssuesPanel from "@/components/cat/QAIssuesPanel";
import { TMSuggestionsPanel } from "@/components/cat/TMSuggestionsPanel";
import { TMHorizontalPanel } from "@/components/cat/TMHorizontalPanel";
import MTSuggestionsPanel from "@/components/cat/MTSuggestionsPanel";
import BatchTranslateDialog from "@/components/cat/BatchTranslateDialog";
import BatchTranslateProgress from "@/components/cat/BatchTranslateProgress";
import {
    batchTranslate,
    type BatchMTProvider,
    type BatchProgress,
    type BatchTranslateResult,
} from "@/lib/cat/batchMTService";
import { SpellCheckPanel } from "@/components/cat/SpellCheckPanel";
import PreviewPanel from "@/components/cat/PreviewPanel";
import {
    TranslyxAIPanel,
    type Message,
} from "@/components/cat/TranslyxAIPanel";
import ExportDialog from "@/components/cat/ExportDialog";
import FindReplaceBar from "@/components/cat/FindReplaceBar";
import { spellChecker, SpellCheckResult } from "@/lib/cat/spellChecker";
import { ExportManager } from "@/lib/cat/exportManager";
import React, {
    useState,
    useEffect,
    useCallback,
    useRef,
    useMemo,
} from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useLyraContext } from "@/context/LyraContext";
import { useProjects } from "@/hooks/useProjects";
import { useAuth } from "@/hooks/useAuth";
import Button from "@/components/common/Button";
import OnboardingModal from "@/components/cat/OnboardingModal";
import UpgradeModal from "@/components/cat/UpgradeModal";
import {
    ArrowLeft,
    Sparkles,
    Keyboard,
    Save,
    Download,
    Settings,
    CheckCircle,
    Clock,
    Target,
    Globe,
    BarChart3,
    AlertCircle,
    RefreshCw,
    Columns,
    List,
    Eye,
    EyeOff,
    Search,
    Filter,
    ChevronUp,
    ChevronDown,
    ChevronLeft,
    ChevronRight,
    Check,
    X,
    Lock,
    Unlock,
    SkipForward,
    SkipBack,
    FileText,
    Copy,
    Star,
    Zap,
    Hash,
    ThumbsUp,
    RotateCcw,
    Send,
} from "lucide-react";
import { supabase } from "@/lib/supabase";
import { linguaprozSupabase } from "@/lib/linguaprozSupabase";
import { isLinguaproz } from "@/config/mode";
import { useLinguaprozAuth } from "@/context/LinguaprozAuthContext";
import { translationMemory } from "@/lib/cat/translationMemory";
import {
    ActiveTimeTracker,
    NO_PROVENANCE,
    provenanceFromSource,
    recordSegmentEvents,
    type SuggestionSource,
} from "@/lib/cat/segmentAnalytics";
import { glossaryService, type GlossaryTerm } from "@/lib/cat/glossaryService";
import {
    getUsage,
    getLimitsForTier,
    getProviderUsage,
    FREE_MT_LIMIT,
    FREE_AI_LIMIT,
} from "@/lib/cat/usageService";
import {
    mtFetch,
    MtLimitError,
    MT_LIMIT_EVENT,
    MT_USAGE_CHANGED_EVENT,
} from "@/lib/mtClient";
import { linguaprozApi } from "@/lib/linguaprozApi";

interface CATSegment {
    id: string;
    source_text: string;
    target_text: string;
    segment_order: number;
    status:
        | "new"
        | "in_progress"
        | "completed"
        | "approved"
        | "confirmed"
        | "rejected";
    locked: boolean;
    notes: string;
    created_at: string;
    updated_at: string;
    source_file?: string;
    page_number?: number | null;
    bbox?: { x: number; y: number; width: number; height: number } | null;
    font_meta?: { size: number; name: string } | null;
    target_origin?: string | null;
    origin_score?: number | null;
    origin_text?: string | null;
    confirmed_at?: string | null;
}

interface CATProject {
    id: string;
    name: string;
    source_language: string;
    target_language: string;
    total_segments: number;
    completed_segments: number;
    word_count: number;
    created_at: string;
    updated_at: string;
}

type LayoutMode = "parallel" | "stacked";
type FilterMode =
    | "all"
    | "new"
    | "in_progress"
    | "completed"
    | "approved"
    | "confirmed"
    | "rejected";

const EditorPage: React.FC = () => {
    const { projectId } = useParams<{ projectId: string }>();
    const navigate = useNavigate();
    const { setEditorContext, setLyraOpen, lyraOpen, setOnApplySegmentEdit, clearEditorContext, resetLyraMessages } = useLyraContext();
    const { user } = useAuth();
    const lpAuth = isLinguaproz
        ? useLinguaprozAuth()
        : { user: null, isAuthenticated: false, isLoading: false };
    const lpUser = lpAuth.user;
    const lpAuthenticated = lpAuth.isAuthenticated;
    const lpLoading = isLinguaproz ? (lpAuth as any).isLoading ?? false : false;
    const db = isLinguaproz ? linguaprozSupabase : supabase;
    const effectiveUser = isLinguaproz ? lpUser : user;
    const effectiveUserId = isLinguaproz
        ? (lpUser?.id ?? "lp-anonymous")
        : (user?.id ?? null);

    const [providerUsage, setProviderUsage] = useState<{
        deepl: number;
        google: number;
        mymemory: number;
    } | null>(null);
    const [project, setProject] = useState<CATProject | null>(null);

    const RTL_LANGUAGES = [
        "ar",
        "arabic",
        "he",
        "hebrew",
        "fa",
        "persian",
        "ur",
        "urdu",
        "ar-sa",
        "ar-eg",
        "ar-ma",
        "yi",
        "yiddish",
        "ps",
        "pashto",
        "sd",
        "sindhi",
    ];
    const isRTLLanguage = (lang: string): boolean => {
        if (!lang) return false;
        return RTL_LANGUAGES.includes(lang.toLowerCase().trim());
    };

    const [segments, setSegments] = useState<CATSegment[]>([]);
    const [selectedFileFilter, setSelectedFileFilter] = useState<string>("all");
    const [availableFiles, setAvailableFiles] = useState<string[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [activeSegmentIndex, setActiveSegmentIndex] = useState(() => {
        try {
            const pid = window.location.pathname.split("/")[2];
            return Number(
                localStorage.getItem(`translyx_editor_segment_${pid}`) || 0,
            );
        } catch {
            return 0;
        }
    });
    const [layoutMode, setLayoutMode] = useState<LayoutMode>("parallel");
    const [filterMode, setFilterMode] = useState<FilterMode>("all");
    const [searchQuery, setSearchQuery] = useState("");
    const [showSourceDebug, setShowSourceDebug] = useState(false);
    const [showSettings, setShowSettings] = useState(false);
    const [showGoToDialog, setShowGoToDialog] = useState(false);
    const [showShortcutsDialog, setShowShortcutsDialog] = useState(false);
    const [showHelpPanel, setShowHelpPanel] = useState(false);
    const [helpSearch, setHelpSearch] = useState("");
    const [openHelpSection, setOpenHelpSection] = useState<string | null>(
        "editor",
    );
    const [showOnboarding, setShowOnboarding] = useState(() => {
        return localStorage.getItem("translyx_onboarding_done") !== "true";
    });
    const [showQAPanel, setShowQAPanel] = useState(false);
    const [qaResult, setQAResult] = useState<QAResult | null>(null);
    const [showTMPanel, setShowTMPanel] = useState(false);
    const [showPreviewPanel, setShowPreviewPanel] = useState(false);
    const [tm, setTM] = useState<boolean>(false);
    const [mtPanelOpen, setMtPanelOpen] = useState(false);
    const [showBatchDialog, setShowBatchDialog] = useState(false);
    const [showBatchProgress, setShowBatchProgress] = useState(false);
    const [batchProvider, setBatchProvider] =
        useState<BatchMTProvider>("deepl");
    const [batchProgress, setBatchProgress] = useState<BatchProgress | null>(
        null,
    );
    const [batchResult, setBatchResult] = useState<BatchTranslateResult | null>(
        null,
    );
    const batchAbortRef = useRef<AbortController | null>(null);
    const [batchAIReviewing, setBatchAIReviewing] = useState(false);
    const [batchAIReviewProgress, setBatchAIReviewProgress] = useState(0);
    const [batchAIReviewDone, setBatchAIReviewDone] = useState<
        "idle" | "pass" | "warn"
    >("idle");
    const [batchAIReviewResults, setBatchAIReviewResults] = useState<
        { order: number; id: string; issue: string }[]
    >([]);
    const [showAIReviewModal, setShowAIReviewModal] = useState(false);
    const [batchAIReviewTotal, setBatchAIReviewTotal] = useState(0);
    const [userUsage, setUserUsage] = useState<{
        mt_chars_used: number;
        ai_queries_used: number;
    } | null>(null);
    const [spellCheckOpen, setSpellCheckOpen] = useState(false);
    const [showKeyboardHelp, setShowKeyboardHelp] = useState(false);
    const [showFindReplace, setShowFindReplace] = useState(false);
    const [findMatchSegmentId, setFindMatchSegmentId] = useState<string>("");
    const [findText, setFindText] = useState<string>("");
    const [findScope, setFindScope] = useState<"target" | "source">("target");
    const [aiPanelOpen, setAiPanelOpen] = useState(() => {
        return localStorage.getItem("translyx_ai_open") === "true";
    });
    const [glossaryTerms, setGlossaryTerms] = useState<GlossaryTerm[]>([]);
    const [aiMessages, setAiMessages] = useState<Message[]>(() => {
        try {
            const stored = localStorage.getItem("translyx_ai_messages");
            return stored ? JSON.parse(stored) : [];
        } catch {
            return [];
        }
    });
    const [spellCheckResults, setSpellCheckResults] =
        useState<SpellCheckResult | null>(null);
    const [exportDialogOpen, setExportDialogOpen] = useState(false);
    const [submitLoading, setSubmitLoading] = useState(false);
    const [submitDialogOpen, setSubmitDialogOpen] = useState(false);
    const [showUpgradeModal, setShowUpgradeModal] = useState(false);
    const [upgradeLimitType, setUpgradeLimitType] = useState<"mt" | "ai">("mt");
    const [userTier, setUserTier] = useState<string>("free");

    // The server counts usage. A 402 from the MT proxy opens the upgrade dialog, and any
    // successful MT or AI call re-reads the numbers shown here.
    useEffect(() => {
        const onLimit = (ev: Event) => {
            const kind = (ev as CustomEvent).detail?.kind === "ai" ? "ai" : "mt";
            setUpgradeLimitType(kind);
            setShowUpgradeModal(true);
        };
        const onUsage = () => {
            if (!effectiveUserId || isLinguaproz) return;
            getUsage(effectiveUserId).then((u) => setUserUsage(u));
            getProviderUsage(effectiveUserId).then((u) => setProviderUsage(u));
        };
        window.addEventListener(MT_LIMIT_EVENT, onLimit);
        window.addEventListener(MT_USAGE_CHANGED_EVENT, onUsage);
        return () => {
            window.removeEventListener(MT_LIMIT_EVENT, onLimit);
            window.removeEventListener(MT_USAGE_CHANGED_EVENT, onUsage);
        };
    }, [effectiveUserId]);

    const [currentPage, setCurrentPage] = useState(() => {
        try {
            const pid = window.location.pathname.split("/")[2];
            return Number(
                localStorage.getItem(`translyx_editor_page_${pid}`) || 1,
            );
        } catch {
            return 1;
        }
    });
    const [itemsPerPage, setItemsPerPage] = useState(() => {
        try {
            const keys = Object.keys(localStorage).filter((k) =>
                k.startsWith("translyxcat_settings_"),
            );
            if (keys.length > 0) {
                const s = JSON.parse(localStorage.getItem(keys[0]) || "{}");
                if (s.items_per_page) return Number(s.items_per_page);
            }
        } catch {}
        return 25;
    });

    const [autoSaveStatus, setAutoSaveStatus] = useState<
        "saved" | "saving" | "error"
    >("saved");
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
    // One autosave timer per segment, so typing in one segment never cancels the
    // pending save of another.
    const saveTimersRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());
    const lastSavedRef = useRef<{ [key: string]: string }>({});
    const textareaRefs = useRef<{
        [key: string]: {
            source: HTMLTextAreaElement | null;
            target: HTMLTextAreaElement | null;
        };
    }>({});
    const segmentsRef = useRef(segments);
    const pendingNavSegmentId = useRef<string | null>(null);

    useEffect(() => {
        segmentsRef.current = segments;
    }, [segments]);
    const saveSegmentNowRef = useRef<(id: string) => Promise<void>>(async () => {});

    useEffect(() => {
        if (!pendingNavSegmentId.current) return;
        const targetId = pendingNavSegmentId.current;
        pendingNavSegmentId.current = null;
        const idx = segments.findIndex((s) => s.id === targetId);
        if (idx === -1) return;
        isProgrammaticNav.current = true;
        setActiveSegmentIndex(idx);
        setCurrentPage(Math.ceil((idx + 1) / itemsPerPage) || 1);
    }, [segments]);

    useEffect(() => {
        if (user || lpAuthenticated) {
            setTM(true);
        }
    }, [user, lpAuthenticated]);

    useEffect(() => {
        if (user?.id) {
            db.from("profiles")
                .select("tier")
                .eq("id", user.id)
                .single()
                .then(({ data }) => {
                    if (data?.tier) setUserTier(data.tier);
                });
        }
    }, [user]);

    useEffect(() => {
        Object.values(textareaRefs.current).forEach((refs) => {
            if (refs.source) {
                refs.source.style.height = "auto";
                refs.source.style.height = refs.source.scrollHeight + "px";
            }
            if (refs.target) {
                refs.target.style.height = "auto";
                refs.target.style.height = refs.target.scrollHeight + "px";
            }
        });
    }, [segments, activeSegmentIndex, layoutMode, filterMode, currentPage]);

    // Segment provenance and confirmation events feed the Analytics page. Not
    // recorded in LP mode.
    const [activeTime] = useState(() => new ActiveTimeTracker());
    useEffect(() => {
        if (isLinguaproz) return;
        const note = () => activeTime.noteActivity();
        const names = ["keydown", "mousemove", "mousedown", "wheel"] as const;
        names.forEach((n) => window.addEventListener(n, note, { passive: true }));
        const timer = window.setInterval(() => activeTime.tick(), 1000);
        return () => {
            names.forEach((n) => window.removeEventListener(n, note));
            window.clearInterval(timer);
        };
    }, [activeTime]);
    const activeSegmentId = segments[activeSegmentIndex]?.id ?? null;
    useEffect(() => {
        if (!isLinguaproz) activeTime.setActive(activeSegmentId);
    }, [activeTime, activeSegmentId]);

    const emitSegmentEvents = (
        event: "confirmed" | "unconfirmed",
        segs: CATSegment[],
    ) => {
        if (isLinguaproz || !user?.id || !project || segs.length === 0) return;
        recordSegmentEvents(
            segs.map((segment) => ({
                userId: user.id,
                projectId: project.id,
                sourceLanguage: project.source_language,
                targetLanguage: project.target_language,
                event,
                segment,
                activeMs: event === "confirmed" ? activeTime.consume(segment.id) : 0,
            })),
        );
    };

    const handleSegmentComplete = async (
        sourceText: string,
        targetText: string,
    ) => {
        if (targetText.trim() && project) {
            await translationMemory.addEntry(
                sourceText,
                targetText,
                project.source_language,
                project.target_language,
                project.id,
            );
        }
    };

    const checkSpelling = async () => {
        const segment = segments[activeSegmentIndex];
        if (!segment || !segment.target_text?.trim() || !project) return;
        try {
            const result = await spellChecker.checkText(segment.target_text, {
                language: project.target_language,
            });
            setSpellCheckResults(result);
            setSpellCheckOpen(true);
        } catch (err) {
            console.error("Spell check failed:", err);
            setSpellCheckOpen(true);
        }
    };

    const logDebug = (message: string, data?: any) => {
        console.log(`\u{1F50D} EditorPage: ${message}`, data || "");
    };

    const handleExport = async (
        format: "xliff" | "docx" | "pdf" | "txt" | "tmx",
        options: {
            includeSource: boolean;
            includeTarget: boolean;
            includeNotes: boolean;
        },
    ) => {
        if (!project) return;
        try {
            const { data: allSegments, error } = await db
                .from("cat_segments")
                .select("*")
                .eq("project_id", project.id)
                .order("segment_order", { ascending: true });
            if (error || !allSegments)
                throw new Error("Failed to load segments");
            const blob = await ExportManager.exportProject(
                allSegments,
                format,
                {
                    projectName: project.name,
                    sourceLanguage: project.source_language,
                    targetLanguage: project.target_language,
                    ...options,
                },
            );
            const url = URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.download = `${project.name.replace(/[^a-zA-Z0-9]/g, "_")}.${format}`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
            alert(`Exported successfully as ${format.toUpperCase()}!`);
            setExportDialogOpen(false);
        } catch (error: any) {
            console.error("Export error:", error);
            alert("Export failed. Please try again.");
        }
    };

    const handleSubmitTranslation = async () => {
        if (!project) return;
        setSubmitLoading(true);
        try {
            // flush any pending autosave first
            if (hasUnsavedChanges) {
                await Promise.all(
                    segments
                        .filter((s) => (s as any)._dirty)
                        .map((s) =>
                            db
                                .from("cat_segments")
                                .update({
                                    target_text: s.target_text,
                                    status: s.status,
                                })
                                .eq("id", s.id),
                        ),
                );
            }
            await linguaprozApi.submitProject(project.id, true);
            (window as any).posthog?.capture("translation_submitted", {
                project_id: project?.id,
            });
            if (isLinguaproz) {
                // Confirmed with TMS — 17 June 2026 — translator's project list
                window.location.href =
                    "https://translators.linguaproz.com/translator/projects";
            } else {
                navigate("/dashboard", {
                    state: { submitted: true, projectName: (project as any).name },
                });
            }
        } catch (err) {
            console.error("Submit failed:", err);
            alert("Submission failed. Please try again or contact support.");
        } finally {
            setSubmitLoading(false);
        }
    };

    const handleSimpleExport = async () => {
        if (!project) return;
        try {
            const xliffContent = generateXLIFF(project, segments);
            const blob = new Blob([xliffContent], { type: "application/xml" });
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `${project.name.replace(/[^a-zA-Z0-9]/g, "_")}.xlf`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
            (window as any).posthog?.capture("project_exported", {
                project_id: project?.id,
            });
        } catch (error) {
            console.error("Export failed:", error);
            alert("Export failed. Please try again.");
        }
    };

    // Everything a segment save writes comes from the segment itself, never from
    // the keystroke that scheduled it, so provenance and status cannot be lost
    // or written stale.
    const segmentSavePayload = (seg: CATSegment) => ({
        target_text: seg.target_text,
        status: seg.status,
        updated_at: new Date().toISOString(),
        ...(isLinguaproz
            ? {}
            : {
                  target_origin: seg.target_origin ?? null,
                  origin_score: seg.origin_score ?? null,
                  origin_text: seg.origin_text ?? null,
                  confirmed_at: seg.confirmed_at ?? null,
              }),
    });

    const cancelPendingSave = (segmentId: string) => {
        const timer = saveTimersRef.current.get(segmentId);
        if (timer) clearTimeout(timer);
        saveTimersRef.current.delete(segmentId);
    };

    const saveSegmentNow = async (segmentId: string) => {
        cancelPendingSave(segmentId);
        const seg = segmentsRef.current.find((s) => s.id === segmentId);
        if (!seg) return;
        try {
            const { error } = await db
                .from("cat_segments")
                .update(segmentSavePayload(seg))
                .eq("id", segmentId);
            if (error) {
                console.error("Save failed:", error);
                setAutoSaveStatus("error");
                return;
            }
            lastSavedRef.current[segmentId] = seg.target_text;
            if (saveTimersRef.current.size === 0) {
                setHasUnsavedChanges(false);
                setAutoSaveStatus("saved");
            }
        } catch (error) {
            console.error("Save error:", error);
            setAutoSaveStatus("error");
        }
    };

    saveSegmentNowRef.current = saveSegmentNow;

    const debouncedSave = (segmentId: string) => {
        cancelPendingSave(segmentId);
        setHasUnsavedChanges(true);
        setAutoSaveStatus("saving");
        saveTimersRef.current.set(
            segmentId,
            setTimeout(() => saveSegmentNow(segmentId), 2000),
        );
    };

    const handleManualSave = async () => {
        if (!hasUnsavedChanges) return;
        setAutoSaveStatus("saving");
        try {
            const updates = segments
                .filter(
                    (segment) =>
                        lastSavedRef.current[segment.id] !==
                        segment.target_text,
                )
                .map((segment) => ({
                    id: segment.id,
                    ...segmentSavePayload({
                        ...segment,
                        status: segment.target_text.trim()
                            ? segment.status === "confirmed"
                                ? "confirmed"
                                : "in_progress"
                            : "new",
                    }),
                }));
            if (updates.length > 0) {
                const promises = updates.map((update) =>
                    db.from("cat_segments").update(update).eq("id", update.id),
                );
                await Promise.all(promises);
                updates.forEach((update) => {
                    cancelPendingSave(update.id);
                    lastSavedRef.current[update.id] = update.target_text;
                });
            }
            setHasUnsavedChanges(false);
            setAutoSaveStatus("saved");
        } catch (error) {
            console.error("Manual save failed:", error);
            setAutoSaveStatus("error");
        }
    };

    const flushAllUnsaved = async (): Promise<void> => {
        const dirtySegments = segments.filter((s) => (s as any)._dirty);
        if (dirtySegments.length === 0) return;
        setAutoSaveStatus("saving");
        try {
            await Promise.all(
                dirtySegments.map((s) =>
                    db
                        .from("cat_segments")
                        .update(segmentSavePayload(s))
                        .eq("id", s.id),
                ),
            );
            setAutoSaveStatus("saved");
        } catch {
            setAutoSaveStatus("error");
        }
    };

    const updateProjectStats = async () => {
        if (!project) return;
        try {
            const { count } = await db
                .from("cat_segments")
                .select("*", { count: "exact", head: true })
                .eq("project_id", project.id)
                .in("status", ["confirmed"]);
            const completedCount = count || 0;
            const { count: totalCount } = await db
                .from("cat_segments")
                .select("*", { count: "exact", head: true })
                .eq("project_id", project.id);
            const percentComplete = totalCount
                ? Math.round((completedCount / totalCount) * 100)
                : 0;
            const { error: updateError } = await db
                .from("cat_projects")
                .update({
                    total_segments: totalCount || 0,
                    completed_segments: completedCount,
                    percent_complete: percentComplete,
                    updated_at: new Date().toISOString(),
                })
                .eq("id", project.id);
            if (updateError) {
                console.error("Failed to update project stats:", updateError);
                return;
            }
            setProject((prev) =>
                prev
                    ? {
                          ...prev,
                          completed_segments: completedCount,
                          percent_complete: percentComplete,
                      }
                    : null,
            );
            console.log("Stats updated:", completedCount);
        } catch (error) {
            console.error("Failed to update project stats:", error);
        }
    };

    const handleSegmentChange = (
        index: number,
        targetText: string,
        source?: SuggestionSource,
    ) => {
        const segment = segments[index];
        if (!segment || segment.locked) return;
        // Provenance: a suggestion sets it, clearing the target resets it, and
        // text typed with no suggestion is "human". Editing a suggestion keeps
        // its origin so the edit distance can be measured at confirmation.
        let prov: typeof NO_PROVENANCE | null = null;
        if (!isLinguaproz) {
            if (!targetText.trim()) prov = NO_PROVENANCE;
            else if (source) prov = provenanceFromSource(source, targetText);
            else if (!segment.target_origin)
                prov = { target_origin: "human", origin_score: null, origin_text: null };
        }
        // Editing a confirmed segment reopens it.
        const reopens = !isLinguaproz && segment.status === "confirmed";
        const extra: Record<string, unknown> = {
            ...(prov || {}),
            ...(reopens ? { confirmed_at: null } : {}),
        };
        const newStatus = targetText.trim() ? "in_progress" : "new";
        setSegments((prev) =>
            prev.map((seg, i) =>
                i === index
                    ? {
                          ...seg,
                          ...extra,
                          target_text: targetText,
                          status: newStatus,
                      }
                    : seg,
            ),
        );
        if (reopens) emitSegmentEvents("unconfirmed", [segment]);
        debouncedSave(segment.id);
    };

    const confirmSegment = (index: number) => {
        const segment = segments[index];
        if (!segment || !segment.target_text.trim()) return;
        updateSegmentStatus(index, "confirmed");
        handleSegmentComplete(segment.source_text, segment.target_text);
        (window as any).posthog?.capture("segment_confirmed", {
            project_id: project?.id,
            segment_index: index,
        });
        const nextIncompleteIndex = segments.findIndex(
            (s, i) => i > index && s.status !== "confirmed",
        );
        if (nextIncompleteIndex !== -1)
            setActiveSegmentIndex(nextIncompleteIndex);
    };

    const confirmAllSegments = async () => {
        const toConfirm = segments.filter(
            (s) =>
                s.target_text?.trim() &&
                s.status !== "confirmed" &&
                !s.locked,
        );
        if (toConfirm.length === 0) return;
        const confirmedAt = new Date().toISOString();
        const stamp = isLinguaproz ? {} : { confirmed_at: confirmedAt };
        setSegments((prev) =>
            prev.map((seg) =>
                toConfirm.find((s) => s.id === seg.id)
                    ? { ...seg, ...stamp, status: "confirmed" as const }
                    : seg,
            ),
        );
        for (const segment of toConfirm)
            handleSegmentComplete(segment.source_text, segment.target_text);
        emitSegmentEvents("confirmed", toConfirm);
        for (const seg of toConfirm) {
            cancelPendingSave(seg.id);
            await db
                .from("cat_segments")
                .update({
                    ...segmentSavePayload({ ...seg, ...stamp, status: "confirmed" }),
                    updated_at: confirmedAt,
                })
                .eq("id", seg.id);
            lastSavedRef.current[seg.id] = seg.target_text;
        }
        await updateProjectStats();
    };

    const reopenAllSegments = async () => {
        const toReopen = segments.filter(
            (s) => s.status === "confirmed",
        );
        if (toReopen.length === 0) return;
        const unstamp = isLinguaproz ? {} : { confirmed_at: null };
        setSegments((prev) =>
            prev.map((seg) =>
                toReopen.find((s) => s.id === seg.id)
                    ? { ...seg, ...unstamp, status: "in_progress" as const }
                    : seg,
            ),
        );
        emitSegmentEvents("unconfirmed", toReopen);
        for (const seg of toReopen) {
            cancelPendingSave(seg.id);
            await db
                .from("cat_segments")
                .update(
                    segmentSavePayload({ ...seg, ...unstamp, status: "in_progress" }),
                )
                .eq("id", seg.id);
        }
        await updateProjectStats();
    };

    const confirmPageSegments = async () => {
        const toConfirm = paginatedSegments.filter(
            (s) =>
                s.target_text?.trim() &&
                s.status !== "confirmed" &&
                !s.locked,
        );
        if (toConfirm.length === 0) return;
        const confirmedAt = new Date().toISOString();
        const stamp = isLinguaproz ? {} : { confirmed_at: confirmedAt };
        setSegments((prev) =>
            prev.map((seg) =>
                toConfirm.find((s) => s.id === seg.id)
                    ? { ...seg, ...stamp, status: "confirmed" as const }
                    : seg,
            ),
        );
        for (const segment of toConfirm)
            handleSegmentComplete(segment.source_text, segment.target_text);
        emitSegmentEvents("confirmed", toConfirm);
        for (const seg of toConfirm) {
            cancelPendingSave(seg.id);
            await db
                .from("cat_segments")
                .update({
                    ...segmentSavePayload({ ...seg, ...stamp, status: "confirmed" }),
                    updated_at: confirmedAt,
                })
                .eq("id", seg.id);
            lastSavedRef.current[seg.id] = seg.target_text;
        }
        await updateProjectStats();
    };

    const updateSegmentStatus = async (
        index: number,
        status: CATSegment["status"],
    ) => {
        const segment = segments[index];
        if (!segment) return;
        // confirmed_at moves with the status; events record the change.
        const wasConfirmed = segment.status === "confirmed";
        const stamp: Record<string, string | null> = isLinguaproz
            ? {}
            : status === "confirmed"
              ? { confirmed_at: new Date().toISOString() }
              : wasConfirmed
                ? { confirmed_at: null }
                : {};
        setSegments((prev) =>
            prev.map((seg, i) =>
                i === index ? { ...seg, ...stamp, status } : seg,
            ),
        );
        if (status === "confirmed" && !wasConfirmed)
            emitSegmentEvents("confirmed", [segment]);
        else if (status !== "confirmed" && wasConfirmed)
            emitSegmentEvents("unconfirmed", [segment]);
        try {
            cancelPendingSave(segment.id);
            const { error } = await db
                .from("cat_segments")
                .update(
                    segmentSavePayload({ ...segment, ...stamp, status } as CATSegment),
                )
                .eq("id", segment.id);
            if (error) throw error;
            lastSavedRef.current[segment.id] = segment.target_text;
            await updateProjectStats();
        } catch (error) {
            console.error("Failed to update segment status:", error);
        }
    };

    const toggleSegmentLock = async (index: number) => {
        const segment = segments[index];
        if (!segment) return;
        const newLocked = !segment.locked;
        setSegments((prev) =>
            prev.map((seg, i) =>
                i === index ? { ...seg, locked: newLocked } : seg,
            ),
        );
        try {
            await db
                .from("cat_segments")
                .update({
                    locked: newLocked,
                    updated_at: new Date().toISOString(),
                })
                .eq("id", segment.id);
        } catch (error) {
            console.error("Failed to toggle lock:", error);
        }
    };

    const translatedSegmentCount = useMemo(() => {
        return segments.filter(
            (s) => s.target_text && s.target_text.trim() !== "",
        ).length;
    }, [segments]);

    const filteredSegments = segments.filter((segment) => {
        if (
            selectedFileFilter !== "all" &&
            segment.source_file !== selectedFileFilter
        )
            return false;
        if (filterMode !== "all" && segment.status !== filterMode) return false;
        if (
            searchQuery &&
            !segment.source_text
                ?.toLowerCase()
                .includes(searchQuery.toLowerCase()) &&
            !segment.target_text
                ?.toLowerCase()
                .includes(searchQuery.toLowerCase())
        )
            return false;
        return true;
    });

    const totalPages = Math.ceil(filteredSegments.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const paginatedSegments = filteredSegments.slice(startIndex, endIndex);

    const dataLoadedRef = useRef(false);
    const isProgrammaticNav = useRef(false);
    useEffect(() => {
        if (!dataLoadedRef.current) return;
        if (isProgrammaticNav.current) {
            isProgrammaticNav.current = false;
            return;
        }
        setCurrentPage(1);
    }, [filterMode, searchQuery, itemsPerPage]);

    const generateXLIFF = (project: CATProject, segments: CATSegment[]) => {
        const xliffHeader = `<?xml version="1.0" encoding="UTF-8"?>\n<xliff version="1.2">\n  <file source-language="${project.source_language}" target-language="${project.target_language}" datatype="plaintext" original="${project.name}">\n    <body>`;
        const xliffSegments = segments
            .map(
                (segment) =>
                    `\n      <trans-unit id="${segment.segment_order}" approved="${segment.status === "approved" ? "yes" : "no"}">\n        <source>${escapeXml(segment.source_text || "")}</source>\n        <target state="${segment.status}">${escapeXml(segment.target_text || "")}</target>\n        ${segment.notes ? `<note>${escapeXml(segment.notes)}</note>` : ""}\n      </trans-unit>`,
            )
            .join("");
        return (
            xliffHeader + xliffSegments + `\n    </body>\n  </file>\n</xliff>`
        );
    };

    const escapeXml = (unsafe: string) =>
        unsafe
            .replace(/&/g, "&amp;")
            .replace(/</g, "&lt;")
            .replace(/>/g, "&gt;")
            .replace(/"/g, "&quot;")
            .replace(/'/g, "&#039;");

    const currentSegment = segments[activeSegmentIndex] || null;

    // Register apply handler in LyraContext
    useEffect(() => {
        setOnApplySegmentEdit(() => (order: number, newText: string) => {
            const seg = segmentsRef.current.find(s => s.segment_order === order);
            if (!seg) return;
            const lyraProv = { target_origin: 'lyra', origin_score: null, origin_text: newText };
            setSegments(prev => prev.map(s =>
                s.segment_order === order ? { ...s, ...lyraProv, target_text: newText } : s
            ));
            lastSavedRef.current[seg.id] = newText;
            cancelPendingSave(seg.id);
            // A Supabase query only runs once it is awaited or given a .then.
            supabase.from('cat_segments')
                .update({ target_text: newText, updated_at: new Date().toISOString(), ...lyraProv })
                .eq('id', seg.id)
                .then(({ error }) => { if (error) console.error('Lyra apply not saved:', error.message) })
        });
        return () => { setOnApplySegmentEdit(null); clearEditorContext(); resetLyraMessages(); };
    }, []);

    // Sync active segment to Lyra
    useEffect(() => {
        if (!project || !currentSegment) return;
        setEditorContext({
            project_name: project.name,
            language_pair: project.source_language + ' -> ' + project.target_language,
            segment_source: currentSegment.source_text || '',
            segment_target: currentSegment.target_text || '',
            segment_index: activeSegmentIndex + 1,
            app_version: '1.0.0-beta',
        });
    }, [activeSegmentIndex, currentSegment?.source_text, currentSegment?.target_text, project?.id]);
    const goToSegmentByIndex = (index: number) => {
        if (index >= 0 && index < segments.length) setActiveSegmentIndex(index);
    };
    const goToSegmentByNumber = (segmentNumber: number) =>
        goToSegmentByIndex(segmentNumber - 1);
    const handleNextSegment = () => {
        if (activeSegmentIndex < segments.length - 1)
            setActiveSegmentIndex(activeSegmentIndex + 1);
    };
    const handlePreviousSegment = () => {
        if (activeSegmentIndex > 0)
            setActiveSegmentIndex(activeSegmentIndex - 1);
    };

    const handleCopySourceToTarget = () => {
        if (!currentSegment || currentSegment.locked) return;
        handleSegmentChange(
            activeSegmentIndex,
            currentSegment.source_text || "",
            { kind: "human" },
        );
    };

    const handleBatchConfirm = async (
        provider: BatchMTProvider,
        emptyOnly: boolean,
    ) => {
        setShowBatchDialog(false);
        setBatchProvider(provider);
        setBatchProgress(null);
        setBatchResult(null);
        setShowBatchProgress(true);
        const abortController = new AbortController();
        batchAbortRef.current = abortController;
        const batchSegments = segments.map((s) => ({
            id: s.id,
            source_text: s.source_text,
            source_tagged_text: (s as any).source_tagged_text || null,
            target_text: s.target_text,
            segment_order: s.segment_order,
        }));
        const result = await batchTranslate({
            segments: batchSegments,
            provider,
            sourceLang: project?.source_language || "en",
            targetLang: project?.target_language || "pt",
            emptyOnly,
            signal: abortController.signal,
            onProgress: (p) => setBatchProgress({ ...p }),
        });
        setBatchResult(result);
        // The server stopped the batch at the plan limit; the upgrade dialog opens from the
        // limit event, so just close the progress window.
        if (result.limitReached) setShowBatchProgress(false);
        (window as any).posthog?.capture("batch_mt_used", {
            project_id: project?.id,
            provider: result.provider,
            segments_translated: result.segmentsTranslated,
            characters_used: result.charactersUsed,
        });
        batchAbortRef.current = null;
        if (Object.keys(result.translated).length > 0) {
            setSegments((prev) =>
                prev.map((seg) => {
                    const t = result.translated[seg.id];
                    return t !== undefined
                        ? {
                              ...seg,
                              ...(isLinguaproz
                                  ? {}
                                  : {
                                        target_origin: `mt_${result.provider}`,
                                        origin_score: null,
                                        origin_text: t.replace(/<[^>]+>/g, ""),
                                    }),
                              target_text: t,
                              status: "in_progress" as const,
                          }
                        : seg;
                }),
            );
            let saveErrors = 0;
            for (const [id, target_text] of Object.entries(result.translated)) {
                cancelPendingSave(id);
                const hasHtmlTags = /<[^>]+>/.test(target_text);
                const plainText = hasHtmlTags
                    ? target_text.replace(/<[^>]+>/g, "")
                    : target_text;
                const updateData: Record<string, string | null> = {
                    target_text: plainText,
                    status: "in_progress",
                    updated_at: new Date().toISOString(),
                };
                if (hasHtmlTags) updateData["target_tagged_text"] = target_text;
                if (!isLinguaproz) {
                    updateData["target_origin"] = `mt_${result.provider}`;
                    updateData["origin_score"] = null;
                    updateData["origin_text"] = plainText;
                }
                const { error: updateError } = await db
                    .from("cat_segments")
                    .update(updateData)
                    .eq("id", id);
                if (updateError) {
                    console.error("Failed to save segment", id, updateError);
                    saveErrors++;
                }
            }
            if (saveErrors === 0) {
                console.log(
                    "Batch MT saved",
                    Object.keys(result.translated).length,
                    "segments to DB",
                );
                if (!isLinguaproz) {
                    // Usage was counted by the server; just read it again for display.
                    getUsage(effectiveUserId!).then((u) => setUserUsage(u));
                    getProviderUsage(effectiveUserId!).then((u) =>
                        setProviderUsage(u),
                    );
                }
                await updateProjectStats();
                const providerKey = `mt_usage_${result.provider}`;
                try {
                    const stored = localStorage.getItem(providerKey);
                    const data = stored
                        ? JSON.parse(stored)
                        : {
                              usage: 0,
                              limit: 500000,
                              lastReset: new Date().toISOString(),
                          };
                    data.usage = (data.usage || 0) + result.charactersUsed;
                    localStorage.setItem(providerKey, JSON.stringify(data));
                } catch (e) {
                    console.error("Failed to update usage:", e);
                }
            } else {
                console.error(saveErrors, "segments failed to save");
            }
        }
    };

    const handleBatchAIReview = async () => {
        if (!project) return;
        const translatedIds = batchResult
            ? Object.keys(batchResult.translated)
            : null;
        const toReview = segments
            .filter(
                (s) =>
                    (translatedIds ? translatedIds.includes(s.id) : true) &&
                    s.target_text?.trim(),
            )
            .slice(0, 30);
        console.log(
            "BatchAIReview: mode",
            translatedIds ? "post-batch" : "standalone",
            "segments to scan:",
            toReview.length,
        );
        if (!toReview.length) {
            alert("No translated segments to review");
            return;
        }
        setShowBatchProgress(false);
        setBatchAIReviewing(true);
        const API_BASE =
            (import.meta as any).env?.VITE_API_URL ||
            (import.meta as any).env?.VITE_API_URL || "https://api.linguaproz.com";
        const flagged: { order: number; issue: string }[] = [];
        let reviewed = 0;
        let limitHit = false; // the server said the AI allowance is used up: stop asking
        const CHUNK = 5;
        for (let i = 0; i < toReview.length; i += CHUNK) {
            const chunk = toReview.slice(i, i + CHUNK);
            await Promise.all(
                chunk.map(async (seg) => {
                    if (limitHit) return;
                    try {
                        const res = await mtFetch(
                            `${API_BASE}/api/translate/ai`,
                            {
                                method: "POST",
                                headers: { "Content-Type": "application/json" },
                                body: JSON.stringify({
                                    messages: [
                                        {
                                            role: "user",
                                            content: `Source (${project.source_language}):\n${seg.source_text}\n\nTranslation (${project.target_language}):\n${seg.target_text}\n\nReturn ONLY JSON: {"status":"pass"|"warn","issue":"one sentence or empty string"}`,
                                        },
                                    ],
                                    system: "You are a translation QA reviewer. Check for meaning shifts, omissions, tone mismatches. Be concise. Return only JSON.",
                                    max_tokens: 120,
                                    tier: "standard",
                                }),
                            },
                        );
                        const data = await res.json();
                        const raw = (data.content?.[0]?.text || "")
                            .replace(/```json|```/g, "")
                            .trim();
                        const parsed = JSON.parse(
                            raw.match(/\{[\s\S]*\}/)?.[0] || "{}",
                        );
                        if (parsed.status === "warn" && parsed.issue) {
                            flagged.push({
                                order: seg.segment_order,
                                issue: parsed.issue,
                            });
                            await db
                                .from("cat_segments")
                                .update({ notes: `AI: ${parsed.issue}` })
                                .eq("id", seg.id);
                            setSegments((prev) =>
                                prev.map((s) =>
                                    s.id === seg.id
                                        ? { ...s, notes: `AI: ${parsed.issue}` }
                                        : s,
                                ),
                            );
                        }
                    } catch (err) {
                        if (err instanceof MtLimitError) limitHit = true;
                    }
                }),
            );
            reviewed += chunk.length;
            setBatchAIReviewProgress(
                Math.round((reviewed / toReview.length) * 100),
            );
        }
        setBatchAIReviewing(false);
        setBatchAIReviewDone(flagged.length > 0 ? "warn" : "pass");
        setBatchAIReviewResults(flagged);
        setBatchAIReviewTotal(toReview.length);
        setShowAIReviewModal(true);
        setTimeout(() => setBatchAIReviewDone("idle"), 5000);
        setBatchAIReviewProgress(0);
    };

    const handleRunQACheck = () => {
        if (!currentSegment || !currentSegment.target_text) return;
        const result = QAChecker.checkSegment(
            currentSegment.source_text || "",
            currentSegment.target_text,
        );
        setQAResult(result);
        setShowQAPanel(true);
    };

    const handleConfirmAndNext = async () => {
        if (!currentSegment) return;
        confirmSegment(activeSegmentIndex);
        const nextIncompleteIndex = segments.findIndex(
            (s, i) => i > activeSegmentIndex && s.status !== "confirmed",
        );
        if (nextIncompleteIndex !== -1)
            setActiveSegmentIndex(nextIncompleteIndex);
        else if (activeSegmentIndex < segments.length - 1)
            setActiveSegmentIndex(activeSegmentIndex + 1);
    };

    const handleTextareaInput = (e: React.FormEvent<HTMLTextAreaElement>) => {
        e.currentTarget.style.height = "auto";
        e.currentTarget.style.height = e.currentTarget.scrollHeight + "px";
    };

    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key === "K") {
                e.preventDefault();
                checkSpelling();
                return;
            }
            if (
                (e.ctrlKey || e.metaKey) &&
                e.shiftKey &&
                e.key.toLowerCase() === "s"
            ) {
                e.preventDefault();
                handleCopySourceToTarget();
                return;
            }
            if ((e.ctrlKey || e.metaKey) && e.key === "t") {
                e.preventDefault();
                setMtPanelOpen((prev) => !prev);
                return;
            }
            if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "g") {
                e.preventDefault();
                setShowGoToDialog(true);
                return;
            }
            if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "q") {
                e.preventDefault();
                handleRunQACheck();
                return;
            }
            if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "m") {
                e.preventDefault();
                setShowTMPanel(!showTMPanel);
                return;
            }
            if (e.key === "F1") {
                e.preventDefault();
                setShowShortcutsDialog(true);
                return;
            }
            if ((e.ctrlKey || e.metaKey) && e.key === "ArrowDown") {
                e.preventDefault();
                handleNextSegment();
                return;
            }
            if ((e.ctrlKey || e.metaKey) && e.key === "ArrowUp") {
                e.preventDefault();
                handlePreviousSegment();
                return;
            }
            if ((e.ctrlKey || e.metaKey) && e.key === "Home") {
                e.preventDefault();
                goToSegmentByNumber(1);
                return;
            }
            if ((e.ctrlKey || e.metaKey) && e.key === "End") {
                e.preventDefault();
                goToSegmentByNumber(segments.length);
                return;
            }
            if ((e.ctrlKey || e.metaKey) && e.key === "Enter") {
                e.preventDefault();
                handleConfirmAndNext();
                return;
            }
            if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "s") {
                e.preventDefault();
                handleManualSave();
                return;
            }
            if ((e.ctrlKey || e.metaKey) && e.key === "h") {
                e.preventDefault();
                setShowFindReplace((prev) => !prev);
                return;
            }
        };
        window.addEventListener("keydown", handleKeyDown);
        return () => window.removeEventListener("keydown", handleKeyDown);
    }, [segments, activeSegmentIndex, currentSegment, showTMPanel]);

    const loadProjectData = async () => {
        console.group("LOADING PROJECT DATA");
        dataLoadedRef.current = false;
        try {
            setLoading(true);
            setError(null);
            if (!projectId || (!effectiveUserId && !isLinguaproz))
                throw new Error("Missing project ID or user authentication");
            logDebug("Loading project details");
            const { data: projectData, error: projectError } = await db
                .from("cat_projects")
                .select("*")
                .eq("id", projectId)
                .eq(
                    isLinguaproz ? "translator_cat_user_id" : "owner_id",
                    isLinguaproz ? lpUser?.id : user?.id,
                )
                .single();
            if (projectError)
                throw new Error(`Project not found: ${projectError.message}`);
            logDebug("Project loaded", projectData);
            setProject(projectData);
            if (effectiveUserId && !isLinguaproz) {
                getUsage(effectiveUserId).then((u) => setUserUsage(u));
            }
            if (effectiveUserId && !isLinguaproz) {
                getProviderUsage(effectiveUserId).then((u) =>
                    setProviderUsage(u),
                );
            }
            glossaryService
                .getAllTerms(
                    projectData.source_language,
                    projectData.target_language,
                )
                .then((terms) => setGlossaryTerms(terms));
            logDebug("Loading segments");
            const { data: segmentsData, error: segmentsError } = await db
                .from("cat_segments")
                .select("*")
                .eq("project_id", projectId)
                .order("segment_order", { ascending: true });
            if (segmentsError)
                throw new Error(
                    `Failed to load segments: ${segmentsError.message}`,
                );
            logDebug("Segments loaded", {
                count: segmentsData?.length || 0,
                expectedCount: projectData.total_segments,
            });
            const processedSegments = (segmentsData || []).map((segment) => ({
                ...segment,
                locked: segment.locked || false,
                notes: segment.notes || "",
                source_file: segment.source_file || "",
                status: segment.status || "new",
            }));

            // Extract unique source files
            const uniqueFiles = Array.from(
                new Set(
                    processedSegments
                        .map((seg) => seg.source_file)
                        .filter(Boolean),
                ),
            );
            setAvailableFiles(uniqueFiles);

            setSegments(processedSegments);
            setSelectedFileFilter("all");
            processedSegments.forEach((segment) => {
                lastSavedRef.current[segment.id] = segment.target_text || "";
            });
            const savedSegment = localStorage.getItem(
                `translyx_editor_segment_${projectId}`,
            );
            const savedPage = localStorage.getItem(
                `translyx_editor_page_${projectId}`,
            );
            if (savedSegment) {
                const savedIndex = parseInt(savedSegment, 10);
                if (
                    !isNaN(savedIndex) &&
                    savedIndex < processedSegments.length
                ) {
                    setActiveSegmentIndex(savedIndex);
                }
            } else {
                const firstIncompleteIndex = processedSegments.findIndex(
                    (s) => s.status !== "confirmed",
                );
                setActiveSegmentIndex(
                    firstIncompleteIndex > 0 ? firstIncompleteIndex : 0,
                );
            }
            if (savedPage) {
                const savedPageNum = parseInt(savedPage, 10);
                if (!isNaN(savedPageNum)) setCurrentPage(savedPageNum);
            } else {
                setCurrentPage(1);
            }
            dataLoadedRef.current = true;
            (window as any).posthog?.capture("project_opened", {
                project_id: projectData.id,
                source_language: projectData.source_language,
                target_language: projectData.target_language,
                segment_count: processedSegments.length,
                word_count: projectData.word_count,
            });
            logDebug("Editor setup complete");
        } catch (error: any) {
            console.error("Loading failed:", error);
            setError(error.message || "Failed to load project");
        } finally {
            setLoading(false);
            console.groupEnd();
        }
    };

    useEffect(() => {
        if (isLinguaproz && lpLoading) return;
        if ((user || (lpAuthenticated && lpUser)) && projectId)
            loadProjectData();
    }, [user?.id, lpUser?.id, projectId, lpAuthenticated, lpLoading]);
    useEffect(() => {
        if (projectId)
            localStorage.setItem(
                `translyx_editor_segment_${projectId}`,
                String(activeSegmentIndex),
            );
    }, [activeSegmentIndex, projectId]);
    const activeSegmentRef = useRef<HTMLDivElement>(null);
    useEffect(() => {
        if (segments.length === 0) return;
        setTimeout(() => {
            const segmentEl = document.querySelector(
                `[data-segment-index="${activeSegmentIndex}"]`,
            );
            if (segmentEl) {
                const rect = segmentEl.getBoundingClientRect();
                const viewportHeight = window.innerHeight;
                const footerHeight = 56; // footer bar height in px
                const isNearBottom =
                    rect.bottom > viewportHeight - footerHeight - 80;
                const isNearTop = rect.top < 80;
                if (isNearBottom || isNearTop) {
                    segmentEl.scrollIntoView({
                        behavior: "smooth",
                        block: "center",
                    });
                }
            }
        }, 150);
    }, [activeSegmentIndex, segments.length]);
    useEffect(() => {
        if (projectId)
            localStorage.setItem(
                `translyx_editor_page_${projectId}`,
                String(currentPage),
            );
    }, [currentPage, projectId]);
    useEffect(() => {
        // Leaving the editor saves whatever is still waiting on a timer.
        return () => {
            const waiting = Array.from(saveTimersRef.current.keys());
            waiting.forEach((id) => saveSegmentNowRef.current(id));
        };
    }, []);
    useEffect(() => {
        const seg = segments[activeSegmentIndex];
        if (!seg?.source_text) return;
    }, [activeSegmentIndex, segments]);

    useEffect(() => {
        const handleBeforeUnload = (e: BeforeUnloadEvent) => {
            if (hasUnsavedChanges) {
                e.preventDefault();
                e.returnValue = "";
            }
        };
        window.addEventListener("beforeunload", handleBeforeUnload);
        return () =>
            window.removeEventListener("beforeunload", handleBeforeUnload);
    }, [hasUnsavedChanges]);

    const stats = {
        total: filteredSegments.length,
        completed: filteredSegments.filter(
            (s) => s.status === "confirmed",
        ).length,
        inProgress: filteredSegments.filter((s) => s.status === "in_progress")
            .length,
        new: filteredSegments.filter((s) => s.status === "new").length,
        translated: filteredSegments.filter(
            (s) =>
                s.status === "in_progress" ||
                s.status === "confirmed",
        ).length,
        progress: segments.length
            ? Math.round(
                  (segments.filter(
                      (s) => s.status === "confirmed",
                  ).length /
                      segments.length) *
                      100,
              )
            : 0,
        translatedProgress: segments.length
            ? Math.round(
                  (segments.filter(
                      (s) =>
                          s.status === "in_progress" ||
                          s.status === "confirmed",
                  ).length /
                      segments.length) *
                      100,
              )
            : 0,
    };

    const formatLanguage = (code: string) => {
        const langNames: Record<string, string> = {
            en: "English",
            es: "Spanish",
            fr: "French",
            de: "German",
            it: "Italian",
            pt: "Portuguese",
            "pt-br": "Portuguese (BR)",
            ru: "Russian",
        };
        return langNames[code] || code.toUpperCase();
    };

    if (loading)
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
                    <h2 className="text-lg font-semibold text-gray-900 mb-2">
                        Loading Project...
                    </h2>
                    <p className="text-gray-600">
                        Please wait while we load your translation project
                    </p>
                </div>
            </div>
        );

    if (error)
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="max-w-md mx-auto text-center">
                    <div className="bg-red-50 border border-red-200 rounded-lg p-6">
                        <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
                        <h2 className="text-lg font-semibold text-red-900 mb-2">
                            Error Loading Project
                        </h2>
                        <p className="text-red-700 mb-4">{error}</p>
                        <div className="space-y-3">
                            <Button
                                variant="secondary"
                                onClick={loadProjectData}
                                leftIcon={<RefreshCw className="h-4 w-4" />}
                            >
                                Retry
                            </Button>
                            <Button
                                variant="primary"
                                leftIcon={<ArrowLeft className="h-4 w-4" />}
                                onClick={() =>
                                    navigate(
                                        isLinguaproz
                                            ? "/dashboard"
                                            : "/projects",
                                    )
                                }
                            >
                                {isLinguaproz
                                    ? "Back to Dashboard"
                                    : "Back to Projects"}
                            </Button>
                        </div>
                    </div>
                </div>
            </div>
        );

    if (!project)
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="text-center">
                    <h2 className="text-lg font-semibold text-gray-900 mb-2">
                        No Project Data
                    </h2>
                    <Button
                        variant="primary"
                        leftIcon={<ArrowLeft className="h-4 w-4" />}
                        onClick={() =>
                            navigate(isLinguaproz ? "/dashboard" : "/projects")
                        }
                    >
                        {isLinguaproz
                            ? "Back to Dashboard"
                            : "Back to Projects"}
                    </Button>
                </div>
            </div>
        );

    return (
        <div className="h-screen flex flex-col overflow-hidden bg-gray-50">
            {/* STICKY Header */}
            <div
                className={`sticky top-0 z-50 flex-shrink-0 bg-slate-900 border-b border-slate-700 shadow-sm transition-all duration-300 ${mtPanelOpen || spellCheckOpen || lyraOpen ? "mr-96" : "mr-0"}`}
            >
                <div className="px-4 py-2">
                    {/* ROW 1: Project info + Save/Export */}
                    <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-3 min-w-0">
                            <Button
                                variant="ghost"
                                className="text-slate-100 hover:text-white hover:bg-slate-700"
                                size="sm"
                                onClick={async () => {
                                    await flushAllUnsaved();
                                    navigate(
                                        isLinguaproz
                                            ? "/dashboard"
                                            : "/projects",
                                    );
                                }}
                            >
                                <ArrowLeft className="h-4 w-4 mr-1" />
                                Back
                            </Button>
                            <div className="h-6 w-px bg-slate-600 shrink-0" />
                            <div className="min-w-0">
                                <h1 className="text-sm font-semibold text-white truncate max-w-xs">
                                    {project.name}
                                </h1>
                                <div className="flex items-center gap-2 text-xs text-slate-400">
                                    <Globe className="h-3 w-3" />
                                    <span>
                                        {formatLanguage(
                                            project.source_language,
                                        )}{" "}
                                        →{" "}
                                        {formatLanguage(
                                            project.target_language,
                                        )}
                                    </span>
                                    <span>·</span>
                                    <span>{segments.length} segments</span>
                                    <span>·</span>
                                    <span>
                                        {(
                                            project.word_count ?? 0
                                        ).toLocaleString()}{" "}
                                        words
                                    </span>
                                </div>
                            </div>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                            {autoSaveStatus === "saving" && (
                                <div className="flex items-center gap-1.5 text-xs text-slate-100">
                                    <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-gold-500" />
                                    <span>Saving…</span>
                                </div>
                            )}
                            {autoSaveStatus === "saved" &&
                                !hasUnsavedChanges && (
                                    <div className="flex items-center gap-1.5 text-xs text-slate-100">
                                        <CheckCircle className="h-3 w-3 text-emerald-400" />
                                        <span>Saved</span>
                                    </div>
                                )}
                            {autoSaveStatus === "error" && (
                                <div className="flex items-center gap-1.5 text-xs text-red-500">
                                    <AlertCircle className="h-3 w-3" />
                                    <span>Save failed</span>
                                </div>
                            )}
                            {!isLinguaproz && (
                                <Button
                                    variant="secondary"
                                    size="sm"
                                    leftIcon={<Download className="h-4 w-4" />}
                                    onClick={() => setExportDialogOpen(true)}
                                    title="Export project"
                                >
                                    Export
                                </Button>
                            )}
                            {isLinguaproz && (
                                <Button
                                    variant="primary"
                                    size="sm"
                                    leftIcon={
                                        submitLoading ? (
                                            <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white" />
                                        ) : (
                                            <Send className="h-4 w-4" />
                                        )
                                    }
                                    onClick={handleSubmitTranslation}
                                    disabled={submitLoading}
                                    title="Submit translation to LínguaProz"
                                    className="bg-gold-500 hover:bg-gold-600 text-white border-gold-500"
                                >
                                    {submitLoading ? "Submitting…" : "Submit"}
                                </Button>
                            )}
                        </div>
                    </div>
                    {/* ROW 2: Grouped tool ribbon */}
                    <div className="flex items-center gap-1 flex-wrap">
                        {/* MEMORY */}
                        <div className="flex flex-col items-center">
                            <div className="flex items-center gap-1">
                                <button
                                    onClick={() => setShowTMPanel(!showTMPanel)}
                                    className={`flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium rounded transition-all border ${showTMPanel ? "text-white bg-gold-600 border-gold-500" : "text-slate-100 bg-slate-800 hover:bg-slate-700 border-slate-600"}`}
                                    title="Translation Memory (Ctrl+M)"
                                >
                                    <Star className="w-3.5 h-3.5" />
                                    <span>TM</span>
                                </button>
                            </div>
                            <span className="text-[9px] text-slate-400 mt-0.5 font-medium tracking-wider">
                                MEMORY
                            </span>
                        </div>
                        <div className="h-8 w-px bg-slate-500 mx-2 shrink-0" />
                        {/* PREVIEW */}
                        <div className="flex flex-col items-center">
                            <div className="flex items-center gap-1">
                                <button
                                    className={`flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium rounded transition-all border ${showPreviewPanel ? "text-white bg-gold-600 border-gold-500" : "text-slate-100 bg-slate-800 hover:bg-slate-700 border-slate-600"}`}
                                    onClick={() =>
                                        setShowPreviewPanel(function (p) {
                                            return !p;
                                        })
                                    }
                                    title="Document Preview"
                                >
                                    <Eye className="w-3.5 h-3.5" />
                                    <span>Preview</span>
                                </button>
                            </div>
                            <span className="text-[9px] text-slate-400 mt-0.5 font-medium tracking-wider">
                                PREVIEW
                            </span>
                        </div>
                        <div className="h-8 w-px bg-slate-500 mx-2 shrink-0" />
                        {/* TRANSLATE */}
                        <div className="flex flex-col items-center">
                            <div className="flex items-center gap-1">
                                <button
                                    onClick={() => {
                                        const opening = !mtPanelOpen;
                                        if (opening) {
                                            setSpellCheckOpen(false);
                                            setLyraOpen(false);
                                            setAiPanelOpen(false);
                                            localStorage.setItem(
                                                "translyx_ai_open",
                                                "false",
                                            );
                                        }
                                        setMtPanelOpen(opening);
                                    }}
                                    className={`flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium rounded transition-all border ${mtPanelOpen ? "text-white bg-gold-600 border-gold-500" : "text-slate-100 bg-slate-800 hover:bg-slate-700 border-slate-600"}`}
                                    title="Machine Translation (Ctrl+T)"
                                >
                                    <Zap className="w-3.5 h-3.5" />
                                    <span>MT</span>
                                </button>
                                <button
                                    onClick={handleBatchAIReview}
                                    disabled={
                                        segments.length === 0 ||
                                        batchAIReviewProgress > 0
                                    }
                                    className={`flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium rounded transition-all border ${batchAIReviewProgress > 0 ? "text-white bg-gold-600 border-gold-500" : segments.length === 0 ? "text-slate-500 bg-slate-800 border-slate-700 cursor-not-allowed opacity-50" : "text-slate-100 bg-slate-800 hover:bg-slate-700 border-slate-600"}`}
                                    title="AI Quality Review"
                                >
                                    {batchAIReviewProgress > 0 ? (
                                        <div className="animate-spin rounded-full h-3.5 w-3.5 border-b-2 border-white" />
                                    ) : (
                                        <CheckCircle className="w-3.5 h-3.5" />
                                    )}
                                    <span>QA</span>
                                </button>
                            </div>
                            <span className="text-[9px] text-slate-400 mt-0.5 font-medium tracking-wider">
                                TRANSLATE
                            </span>
                        </div>
                        <div className="h-8 w-px bg-slate-500 mx-2 shrink-0" />
                        {/* REVIEW */}
                        <div className="flex flex-col items-center">
                            <div className="flex items-center gap-1">
                                <button
                                    onClick={() => {
                                        const opening = !spellCheckOpen;
                                        if (opening) {
                                            setMtPanelOpen(false);
                                            setLyraOpen(false);
                                            setAiPanelOpen(false);
                                            localStorage.setItem(
                                                "translyx_ai_open",
                                                "false",
                                            );
                                        }
                                        setSpellCheckOpen(opening);
                                    }}
                                    className={`flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium rounded transition-all border ${spellCheckOpen ? "text-white bg-gold-600 border-gold-500" : "text-slate-100 bg-slate-800 hover:bg-slate-700 border-slate-600"}`}
                                    title="Spell Check (Ctrl+Shift+K)"
                                >
                                    <CheckCircle className="w-3.5 h-3.5" />
                                    <span>Spell</span>
                                </button>
                                <button
                                    onClick={() => {
                                        const opening = !lyraOpen;
                                        if (opening) {
                                            setMtPanelOpen(false);
                                            setSpellCheckOpen(false);
                                        }
                                        setLyraOpen(opening);
                                    }}
                                    className={`flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium rounded transition-all border ${lyraOpen ? "text-white bg-gold-600 border-gold-500" : "text-slate-100 bg-slate-800 hover:bg-slate-700 border-slate-600"}`}
                                    title="Ask Lyra"
                                >
                                    <Sparkles className="w-3.5 h-3.5" />
                                    <span>Lyra</span>
                                </button>
                            </div>
                            <span className="text-[9px] text-slate-400 mt-0.5 font-medium tracking-wider">
                                REVIEW
                            </span>
                        </div>
                        <div className="h-8 w-px bg-slate-500 mx-2 shrink-0" />
                        {/* VIEW */}
                        <div className="flex flex-col items-center">
                            <div className="flex items-center gap-1">
                                <select
                                    value={filterMode}
                                    onChange={(e) =>
                                        setFilterMode(
                                            e.target.value as FilterMode,
                                        )
                                    }
                                    className="border border-slate-600 rounded px-1.5 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-gold-500 bg-slate-800 text-white"
                                >
                                    <option value="all">All</option>
                                    <option value="new">New</option>
                                    <option value="in_progress">
                                        In Progress
                                    </option>
                                    <option value="confirmed">Confirmed</option>
                                    <option value="rejected">Rejected</option>
                                </select>
                                <select
                                    value={itemsPerPage}
                                    onChange={(e) =>
                                        setItemsPerPage(Number(e.target.value))
                                    }
                                    className="border border-slate-600 rounded px-1.5 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-gold-500 bg-slate-800 text-white"
                                >
                                    <option value="10">10</option>
                                    <option value="25">25</option>
                                    <option value="50">50</option>
                                    <option value="100">100</option>
                                    <option value={filteredSegments.length}>
                                        All
                                    </option>
                                </select>
                                <div className="flex items-center bg-slate-700 rounded p-0.5">
                                    <button
                                        onClick={() =>
                                            setLayoutMode("parallel")
                                        }
                                        className={`p-1 rounded transition-colors ${layoutMode === "parallel" ? "bg-slate-500 text-gold-400" : "text-slate-300 hover:text-white"}`}
                                        title="Parallel view"
                                    >
                                        <Columns className="h-3.5 w-3.5" />
                                    </button>
                                    <button
                                        onClick={() => setLayoutMode("stacked")}
                                        className={`p-1 rounded transition-colors ${layoutMode === "stacked" ? "bg-slate-500 text-gold-400" : "text-slate-300 hover:text-white"}`}
                                        title="Stacked view"
                                    >
                                        <List className="h-3.5 w-3.5" />
                                    </button>
                                </div>
                                <button
                                    onClick={() =>
                                        setShowFindReplace((prev) => !prev)
                                    }
                                    className={`p-1 rounded transition-colors ${showFindReplace ? "bg-slate-500 text-gold-400" : "text-slate-300 hover:text-white"}`}
                                    title="Find & Replace (Ctrl+H)"
                                >
                                    <Search className="h-3.5 w-3.5" />
                                </button>
                            </div>
                            <span className="text-[9px] text-slate-400 mt-0.5 font-medium tracking-wider">
                                VIEW
                            </span>
                        </div>
                        <div className="h-8 w-px bg-slate-500 mx-2 shrink-0" />
                        {/* NAVIGATE */}
                        <div className="flex flex-col items-center">
                            <div className="flex items-center gap-1">
                                <button
                                    onClick={handlePreviousSegment}
                                    disabled={activeSegmentIndex === 0}
                                    className="p-1.5 rounded text-slate-300 hover:bg-slate-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                                    title="Previous segment (Ctrl+↑)"
                                >
                                    <ChevronUp className="h-3.5 w-3.5" />
                                </button>
                                <button
                                    onClick={handleNextSegment}
                                    disabled={
                                        activeSegmentIndex ===
                                        segments.length - 1
                                    }
                                    className="p-1.5 rounded text-slate-300 hover:bg-slate-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                                    title="Next segment (Ctrl+↓)"
                                >
                                    <ChevronDown className="h-3.5 w-3.5" />
                                </button>
                                <button
                                    onClick={confirmAllSegments}
                                    disabled={
                                        !segments.some(
                                            (s) =>
                                                s.target_text?.trim() &&
                                                s.status !== "confirmed" &&
                                                !s.locked,
                                        )
                                    }
                                    className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium rounded border text-emerald-400 bg-slate-800 hover:bg-slate-700 border-slate-600 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
                                    title="Confirm all translated segments"
                                >
                                    <CheckCircle className="w-3.5 h-3.5" />
                                    <span>All</span>
                                </button>
                                <button
                                    onClick={confirmPageSegments}
                                    disabled={
                                        !paginatedSegments.some(
                                            (s) =>
                                                s.target_text?.trim() &&
                                                s.status !== "confirmed" &&
                                                !s.locked,
                                        )
                                    }
                                    className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium rounded border text-teal-400 bg-slate-800 hover:bg-slate-700 border-slate-600 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
                                    title="Confirm all translated segments on this page"
                                >
                                    <CheckCircle className="w-3.5 h-3.5" />
                                    <span>Page</span>
                                </button>
                                <button
                                    onClick={reopenAllSegments}
                                    disabled={
                                        !segments.some(
                                            (s) => s.status === "confirmed",
                                        )
                                    }
                                    className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium rounded border text-amber-400 bg-slate-800 hover:bg-slate-700 border-slate-600 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
                                    title="Reopen all confirmed segments"
                                >
                                    <RotateCcw className="w-3.5 h-3.5" />
                                    <span>All</span>
                                </button>
                                <button
                                    onClick={() => setShowShortcutsDialog(true)}
                                    className="p-1.5 rounded border text-slate-300 bg-slate-800 hover:bg-slate-700 border-slate-600 transition-colors"
                                    title="Keyboard shortcuts (F1)"
                                >
                                    <Keyboard className="w-3.5 h-3.5" />
                                </button>
                            </div>
                            <span className="text-[9px] text-slate-400 mt-0.5 font-medium tracking-wider">
                                NAVIGATE
                            </span>
                        </div>
                        <div className="h-8 w-px bg-slate-500 mx-2 shrink-0" />
                        {/* FILE FILTER */}
                        {availableFiles.length > 0 && (
                            <div className="flex flex-col items-center">
                                <div className="relative">
                                    <select
                                        value={selectedFileFilter}
                                        onChange={(e) =>
                                            setSelectedFileFilter(
                                                e.target.value,
                                            )
                                        }
                                        className="p-1.5 rounded border bg-slate-800 border-slate-600 text-slate-300 text-xs focus:outline-none focus:ring-1 focus:ring-gold-500 appearance-none pr-6"
                                        title="Filter by source file"
                                    >
                                        <option value="all">All files</option>
                                        {availableFiles.map((fileName) => (
                                            <option
                                                key={fileName}
                                                value={fileName}
                                            >
                                                {fileName.length > 20
                                                    ? fileName.substring(
                                                          0,
                                                          17,
                                                      ) + "..."
                                                    : fileName}
                                            </option>
                                        ))}
                                    </select>
                                    <ChevronDown className="absolute right-1.5 top-1/2 transform -translate-y-1/2 h-3 w-3 text-slate-400 pointer-events-none" />
                                </div>
                                <span className="text-[9px] text-slate-400 mt-0.5 font-medium tracking-wider">
                                    FILE
                                </span>
                            </div>
                        )}

                        {/* HELP */}
                        <div className="flex flex-col items-center">
                            <button
                                onClick={() => setShowHelpPanel(!showHelpPanel)}
                                className={`p-1.5 rounded border transition-all ${showHelpPanel ? "bg-gold-600 border-gold-500 text-white" : "text-slate-300 bg-slate-800 hover:bg-slate-700 border-slate-600"}`}
                                title="Help & Guide"
                            >
                                <img
                                    src="https://cdn-icons-png.flaticon.com/128/1545/1545757.png"
                                    className="w-3.5 h-3.5"
                                    style={{
                                        filter: showHelpPanel
                                            ? "brightness(0) invert(1)"
                                            : "invert(0.7)",
                                    }}
                                    alt="Help"
                                />
                            </button>
                            <span className="text-[9px] text-slate-400 mt-0.5 font-medium tracking-wider">
                                HELP
                            </span>
                        </div>
                        {/* USAGE */}
                        {userUsage && (
                            <div className="ml-auto flex flex-col items-end">
                                <div className="flex items-center gap-2 text-[10px] text-slate-500">
                                    <span title="Machine translation characters used this month">
                                        MT{" "}
                                        {userUsage.mt_chars_used.toLocaleString()}{" "}
                                        /{" "}
                                        {getLimitsForTier(
                                            userTier,
                                        ).mt_chars.toLocaleString()}
                                    </span>
                                    <span>·</span>
                                    <span title="Lyra queries used this month">
                                        Lyra {userUsage.ai_queries_used} /{" "}
                                        {getLimitsForTier(userTier).ai_queries}
                                    </span>
                                </div>
                                <span className="text-[9px] text-slate-400 mt-0.5 font-medium tracking-wider">
                                    USAGE
                                </span>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* PROGRESS BAR */}
            <div
                className={`flex-shrink-0 bg-slate-900 border-b border-slate-700 px-6 py-2 transition-all duration-300 text-white ${mtPanelOpen || spellCheckOpen || lyraOpen ? "mr-96" : "mr-0"}`}
            >
                <div className="flex justify-between text-xs font-semibold text-white mb-1">
                    <span>
                        <span className="text-white font-semibold">
                            {stats.translated}/{stats.total} segments translated
                        </span>
                        {stats.completed > 0 && (
                            <span className="text-emerald-300 ml-2 font-semibold">
                                · {stats.completed} confirmed
                            </span>
                        )}
                    </span>
                    <span
                        className={`font-bold ${stats.progress === 100 ? "text-emerald-300" : "text-gold-300"}`}
                    >
                        {stats.translatedProgress}%
                    </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2 relative overflow-hidden">
                    <div
                        className="absolute top-0 left-0 bg-gold-400 h-2 rounded-full transition-all duration-300"
                        style={{ width: `${stats.translatedProgress}%` }}
                    />
                    <div
                        className="absolute top-0 left-0 bg-emerald-500 h-2 rounded-full transition-all duration-300"
                        style={{ width: `${stats.progress}%` }}
                    />
                </div>
            </div>

            {/* Find & Replace Bar */}
            {showFindReplace && (
                <FindReplaceBar
                    segments={segments}
                    onReplaceOne={(segId, newText) => {
                        setSegments((prev) =>
                            prev.map((s) =>
                                s.id === segId
                                    ? { ...s, target_text: newText }
                                    : s,
                            ),
                        );
                        lastSavedRef.current[segId] = newText;
                        supabase
                            .from("cat_segments")
                            .update({
                                target_text: newText,
                                updated_at: new Date().toISOString(),
                            })
                            .eq("id", segId)
                            .then(() => setAutoSaveStatus("saved"))
                            .catch(() => setAutoSaveStatus("error"));
                    }}
                    onReplaceAll={(replacements) => {
                        setSegments((prev) =>
                            prev.map((s) =>
                                replacements[s.id]
                                    ? { ...s, target_text: replacements[s.id] }
                                    : s,
                            ),
                        );
                        const replacePromises = Object.entries(
                            replacements,
                        ).map(([id, text]) => {
                            lastSavedRef.current[id] = text;
                            return supabase
                                .from("cat_segments")
                                .update({
                                    target_text: text,
                                    updated_at: new Date().toISOString(),
                                })
                                .eq("id", id);
                        });
                        Promise.all(replacePromises)
                            .then(() => {
                                setAutoSaveStatus("saved");
                                setHasUnsavedChanges(false);
                            })
                            .catch(() => setAutoSaveStatus("error"));
                    }}
                    onClose={() => {
                        setShowFindReplace(false);
                        setFindMatchSegmentId("");
                        setFindText("");
                    }}
                    onMatchChange={(segmentId) => {
                        const idx = segments.findIndex(
                            (s) => s.id === segmentId,
                        );
                        if (idx !== -1) {
                            setActiveSegmentIndex(idx);
                            const page = Math.floor(idx / itemsPerPage) + 1;
                            setCurrentPage(page);
                        }
                        setFindMatchSegmentId(segmentId);
                    }}
                    onFindTextChange={setFindText}
                    onScopeChange={setFindScope}
                />
            )}

            {/* Main Content Area */}
            <div className="flex-1 overflow-hidden relative">
                <div
                    className={`h-full overflow-y-auto p-6 transition-margin duration-300 ${mtPanelOpen || spellCheckOpen || lyraOpen ? "mr-96" : "mr-0"}`}
                >
                    <div
                        className={`max-w-6xl mx-auto space-y-3 ${showTMPanel ? "pb-32" : "pb-6"}`}
                    >
                        {paginatedSegments.map((segment, paginatedIndex) => {
                            const actualIndex =
                                filteredSegments.indexOf(segment);
                            return (
                                <div
                                    key={segment.id}
                                    className={`bg-white rounded-lg border transition-all duration-200 cursor-pointer ${findMatchSegmentId === segment.id && showFindReplace ? "ring-2 ring-blue-500 border-blue-400" : actualIndex === activeSegmentIndex ? "ring-2 ring-gold-600 border-amber-200" : "border-gray-200 hover:border-gray-300"} ${segment.status === "confirmed" ? "bg-green-100 border-green-300" : ""} ${segment.status === "in_progress" ? "bg-amber-50 border-amber-200" : ""} ${segment.status === "rejected" ? "bg-red-50 border-red-200" : ""} ${segment.locked ? "bg-gray-100 border-gray-400" : ""}`}
                                    onClick={() =>
                                        setActiveSegmentIndex(actualIndex)
                                    }
                                    data-segment-index={actualIndex}
                                >
                                    <div className="flex items-start">
                                        <div
                                            className={`flex-shrink-0 w-16 py-3 px-2 border-r border-gray-200 flex flex-col items-center relative ${segment.locked ? "bg-green-50" : segment.status === "in_progress" && segment.notes?.includes("TM match") ? "bg-amber-50" : segment.status === "new" && !segment.target_text ? "bg-red-50" : ""}`}
                                        >
                                            {segment.locked && (
                                                <div className="absolute left-0 top-0 bottom-0 w-1 bg-green-400 rounded-l" />
                                            )}
                                            {!segment.locked &&
                                                segment.status ===
                                                    "in_progress" &&
                                                segment.notes?.includes(
                                                    "TM match",
                                                ) && (
                                                    <div className="absolute left-0 top-0 bottom-0 w-1 bg-gold-400 rounded-l" />
                                                )}
                                            {segment.status === "new" &&
                                                !segment.target_text && (
                                                    <div className="absolute left-0 top-0 bottom-0 w-1 bg-red-400 rounded-l" />
                                                )}
                                            <div className="flex items-center justify-center w-10 h-10 rounded-full bg-gray-100 text-sm font-bold text-gray-700">
                                                {segment.segment_order}
                                            </div>
                                            {segment.source_file && (
                                                <div className="mt-1 px-1.5 py-0.5 bg-gray-200 rounded-full">
                                                    <span
                                                        className="text-[9px] text-gray-700 truncate max-w-[36px] block"
                                                        title={
                                                            segment.source_file
                                                        }
                                                    >
                                                        {segment.source_file
                                                            .length > 20
                                                            ? segment.source_file.substring(
                                                                  0,
                                                                  17,
                                                              ) + "..."
                                                            : segment.source_file}
                                                    </span>
                                                </div>
                                            )}
                                            <div className="w-10 h-px bg-gray-200 my-2"></div>
                                            <div className="flex flex-col items-center space-y-1">
                                                <Clock className="h-4 w-4 text-gray-600" />
                                                <span className="text-xs text-gray-700 font-medium text-center leading-tight">
                                                    {segment.source_text
                                                        ?.split(" ")
                                                        .filter((w) => w.trim())
                                                        .length || 0}
                                                </span>
                                                <span className="text-[10px] text-gray-600">
                                                    words
                                                </span>
                                            </div>
                                            {segment.locked && (
                                                <>
                                                    <div className="w-10 h-px bg-gray-200 my-2"></div>
                                                    <Lock className="h-4 w-4 text-gray-600" />
                                                </>
                                            )}
                                        </div>
                                        <div className="flex-1 p-4">
                                            {layoutMode === "parallel" ? (
                                                <div className="grid grid-cols-2 gap-4">
                                                    <div>
                                                        <label className="block text-xs font-medium text-gray-700 mb-1">
                                                            Source (
                                                            {formatLanguage(
                                                                project.source_language,
                                                            )}
                                                            )
                                                            {actualIndex ===
                                                                activeSegmentIndex &&
                                                                glossaryTerms.length >
                                                                    0 &&
                                                                (() => {
                                                                    const matches =
                                                                        glossaryService.findMatches(
                                                                            segment.source_text ||
                                                                                "",
                                                                            glossaryTerms,
                                                                        );
                                                                    if (
                                                                        matches.length ===
                                                                        0
                                                                    )
                                                                        return null;
                                                                    return (
                                                                        <span className="ml-2 inline-flex gap-1 flex-wrap">
                                                                            {matches
                                                                                .slice(
                                                                                    0,
                                                                                    4,
                                                                                )
                                                                                .map(
                                                                                    (
                                                                                        m,
                                                                                        i,
                                                                                    ) => (
                                                                                        <span
                                                                                            key={
                                                                                                i
                                                                                            }
                                                                                            title={`${m.term.term_source} -> ${m.term.term_target}${m.term.domain ? ` (${m.term.domain})` : ""}`}
                                                                                            className="px-1.5 py-0.5 bg-amber-100 text-amber-700 border border-amber-300 rounded text-xs cursor-help"
                                                                                        >
                                                                                            {
                                                                                                m
                                                                                                    .term
                                                                                                    .term_source
                                                                                            }
                                                                                        </span>
                                                                                    ),
                                                                                )}
                                                                        </span>
                                                                    );
                                                                })()}
                                                        </label>
                                                        <textarea
                                                            ref={(el) => {
                                                                if (
                                                                    !textareaRefs
                                                                        .current[
                                                                        segment
                                                                            .id
                                                                    ]
                                                                )
                                                                    textareaRefs.current[
                                                                        segment.id
                                                                    ] = {
                                                                        source: null,
                                                                        target: null,
                                                                    };
                                                                textareaRefs.current[
                                                                    segment.id
                                                                ].source = el;
                                                            }}
                                                            value={
                                                                segment.source_text ||
                                                                ""
                                                            }
                                                            readOnly
                                                            className="w-full border border-gray-200 rounded-lg p-3 bg-gray-50 text-gray-900 resize-none min-h-[60px] text-sm"
                                                            style={{
                                                                height: "auto",
                                                                minHeight:
                                                                    "60px",
                                                            }}
                                                            onInput={
                                                                handleTextareaInput
                                                            }
                                                        />
                                                    </div>
                                                    <div>
                                                        <label className="block text-xs font-medium text-gray-700 mb-1">
                                                            Translation (
                                                            {formatLanguage(
                                                                project.target_language,
                                                            )}
                                                            )
                                                        </label>
                                                        <textarea
                                                            ref={(el) => {
                                                                if (
                                                                    !textareaRefs
                                                                        .current[
                                                                        segment
                                                                            .id
                                                                    ]
                                                                )
                                                                    textareaRefs.current[
                                                                        segment.id
                                                                    ] = {
                                                                        source: null,
                                                                        target: null,
                                                                    };
                                                                textareaRefs.current[
                                                                    segment.id
                                                                ].target = el;
                                                            }}
                                                            value={
                                                                segment.target_text ||
                                                                ""
                                                            }
                                                            onChange={(e) => {
                                                                handleSegmentChange(
                                                                    actualIndex,
                                                                    e.target
                                                                        .value,
                                                                );
                                                                handleTextareaInput(
                                                                    e,
                                                                );
                                                            }}
                                                            disabled={
                                                                segment.locked
                                                            }
                                                            className={`w-full border border-gray-300 rounded-lg p-3 focus:outline-none focus:ring-2 focus:ring-gold-600 focus:border-transparent resize-none min-h-[60px] text-sm ${segment.locked ? "bg-gray-100 cursor-not-allowed" : ""} ${isRTLLanguage(project?.target_language || "") ? "text-right" : ""}`}
                                                            dir={
                                                                isRTLLanguage(
                                                                    project?.target_language ||
                                                                        "",
                                                                )
                                                                    ? "rtl"
                                                                    : "ltr"
                                                            }
                                                            style={{
                                                                height: "auto",
                                                                minHeight:
                                                                    "60px",
                                                            }}
                                                            onInput={
                                                                handleTextareaInput
                                                            }
                                                            placeholder="Enter your translation here..."
                                                        />
                                                    </div>
                                                </div>
                                            ) : (
                                                <div className="space-y-3">
                                                    <div>
                                                        <label className="block text-xs font-medium text-gray-700 mb-1">
                                                            Source (
                                                            {formatLanguage(
                                                                project.source_language,
                                                            )}
                                                            )
                                                            {actualIndex ===
                                                                activeSegmentIndex &&
                                                                glossaryTerms.length >
                                                                    0 &&
                                                                (() => {
                                                                    const matches =
                                                                        glossaryService.findMatches(
                                                                            segment.source_text ||
                                                                                "",
                                                                            glossaryTerms,
                                                                        );
                                                                    if (
                                                                        matches.length ===
                                                                        0
                                                                    )
                                                                        return null;
                                                                    return (
                                                                        <span className="ml-2 inline-flex gap-1 flex-wrap">
                                                                            {matches
                                                                                .slice(
                                                                                    0,
                                                                                    4,
                                                                                )
                                                                                .map(
                                                                                    (
                                                                                        m,
                                                                                        i,
                                                                                    ) => (
                                                                                        <span
                                                                                            key={
                                                                                                i
                                                                                            }
                                                                                            title={`${m.term.term_source} -> ${m.term.term_target}${m.term.domain ? ` (${m.term.domain})` : ""}`}
                                                                                            className="px-1.5 py-0.5 bg-amber-100 text-amber-700 border border-amber-300 rounded text-xs cursor-help"
                                                                                        >
                                                                                            {
                                                                                                m
                                                                                                    .term
                                                                                                    .term_source
                                                                                            }
                                                                                        </span>
                                                                                    ),
                                                                                )}
                                                                        </span>
                                                                    );
                                                                })()}
                                                        </label>
                                                        <textarea
                                                            ref={(el) => {
                                                                if (
                                                                    !textareaRefs
                                                                        .current[
                                                                        segment
                                                                            .id
                                                                    ]
                                                                )
                                                                    textareaRefs.current[
                                                                        segment.id
                                                                    ] = {
                                                                        source: null,
                                                                        target: null,
                                                                    };
                                                                textareaRefs.current[
                                                                    segment.id
                                                                ].source = el;
                                                            }}
                                                            value={
                                                                segment.source_text ||
                                                                ""
                                                            }
                                                            readOnly
                                                            className="w-full border border-gray-200 rounded-lg p-3 bg-gray-50 text-gray-900 resize-none min-h-[60px] text-sm"
                                                            style={{
                                                                height: "auto",
                                                                minHeight:
                                                                    "60px",
                                                            }}
                                                            onInput={
                                                                handleTextareaInput
                                                            }
                                                        />
                                                    </div>
                                                    <div>
                                                        <label className="block text-xs font-medium text-gray-700 mb-1">
                                                            Translation (
                                                            {formatLanguage(
                                                                project.target_language,
                                                            )}
                                                            )
                                                        </label>
                                                        <textarea
                                                            ref={(el) => {
                                                                if (
                                                                    !textareaRefs
                                                                        .current[
                                                                        segment
                                                                            .id
                                                                    ]
                                                                )
                                                                    textareaRefs.current[
                                                                        segment.id
                                                                    ] = {
                                                                        source: null,
                                                                        target: null,
                                                                    };
                                                                textareaRefs.current[
                                                                    segment.id
                                                                ].target = el;
                                                            }}
                                                            value={
                                                                segment.target_text ||
                                                                ""
                                                            }
                                                            onChange={(e) => {
                                                                handleSegmentChange(
                                                                    actualIndex,
                                                                    e.target
                                                                        .value,
                                                                );
                                                                handleTextareaInput(
                                                                    e,
                                                                );
                                                            }}
                                                            disabled={
                                                                segment.locked
                                                            }
                                                            className={`w-full border border-gray-300 rounded-lg p-3 focus:outline-none focus:ring-2 focus:ring-gold-600 focus:border-transparent resize-none min-h-[60px] text-sm ${segment.locked ? "bg-gray-100 cursor-not-allowed" : ""} ${isRTLLanguage(project?.target_language || "") ? "text-right" : ""}`}
                                                            dir={
                                                                isRTLLanguage(
                                                                    project?.target_language ||
                                                                        "",
                                                                )
                                                                    ? "rtl"
                                                                    : "ltr"
                                                            }
                                                            style={{
                                                                height: "auto",
                                                                minHeight:
                                                                    "60px",
                                                            }}
                                                            onInput={
                                                                handleTextareaInput
                                                            }
                                                            placeholder="Enter your translation here..."
                                                        />
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                        <div className="flex-shrink-0 w-10 py-2 px-2 border-l border-gray-200 flex flex-col items-center space-y-2">
                                            {segment.status !== "confirmed" &&
                                            !segment.locked ? (
                                                <>
                                                    <button
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            confirmSegment(
                                                                actualIndex,
                                                            );
                                                        }}
                                                        disabled={
                                                            !segment.target_text?.trim()
                                                        }
                                                        className="group relative flex items-center justify-center w-6 h-6 bg-green-600 hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white rounded-lg transition-all shadow-sm hover:shadow-md"
                                                        title="Confirm segment (Ctrl+Enter)"
                                                    >
                                                        <Check className="h-5 w-5" />
                                                        <span className="absolute left-full ml-2 px-2 py-1 bg-gray-900 text-white text-xs rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10">
                                                            Confirm (Ctrl+Enter)
                                                        </span>
                                                    </button>
                                                    <button
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            updateSegmentStatus(
                                                                actualIndex,
                                                                "confirmed",
                                                            );
                                                        }}
                                                        className="group relative flex items-center justify-center w-6 h-6 bg-blue-600 hover:bg-blue-900 text-white rounded-lg transition-all shadow-sm hover:shadow-md"
                                                        title="Approve segment"
                                                    >
                                                        <ThumbsUp className="h-5 w-5" />
                                                        <span className="absolute left-full ml-2 px-2 py-1 bg-gray-900 text-white text-xs rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10">
                                                            Approve
                                                        </span>
                                                    </button>
                                                </>
                                            ) : (
                                                <button
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        updateSegmentStatus(
                                                            actualIndex,
                                                            "in_progress",
                                                        );
                                                    }}
                                                    className="group relative flex items-center justify-center w-6 h-6 bg-gray-600 hover:bg-gray-700 text-white rounded-lg transition-all shadow-sm hover:shadow-md"
                                                    title="Reopen segment"
                                                >
                                                    <RefreshCw className="h-5 w-5" />
                                                    <span className="absolute left-full ml-2 px-2 py-1 bg-gray-900 text-white text-xs rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10">
                                                        Reopen
                                                    </span>
                                                </button>
                                            )}
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    toggleSegmentLock(
                                                        actualIndex,
                                                    );
                                                }}
                                                className="group relative flex items-center justify-center w-6 h-6 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg transition-all shadow-sm hover:shadow-md"
                                                title={
                                                    segment.locked
                                                        ? "Unlock segment"
                                                        : "Lock segment"
                                                }
                                            >
                                                {segment.locked ? (
                                                    <Lock className="h-5 w-5" />
                                                ) : (
                                                    <Unlock className="h-5 w-5" />
                                                )}
                                                <span className="absolute left-full ml-2 px-2 py-1 bg-gray-900 text-white text-xs rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10">
                                                    {segment.locked
                                                        ? "Unlock"
                                                        : "Lock"}
                                                </span>
                                            </button>
                                            <div className="text-xs text-gray-600 font-medium mt-2">
                                                {actualIndex + 1}/
                                                {segments.length}
                                            </div>
                                        </div>
                                    </div>
                                    {showFindReplace &&
                                        findMatchSegmentId === segment.id &&
                                        findText &&
                                        (() => {
                                            const text =
                                                findScope === "target"
                                                    ? segment.target_text || ""
                                                    : segment.source_text;
                                            const matchIndex = text
                                                .toLowerCase()
                                                .indexOf(
                                                    findText.toLowerCase(),
                                                );
                                            if (matchIndex === -1) return null;
                                            const contextStart = Math.max(
                                                0,
                                                matchIndex - 40,
                                            );
                                            const contextEnd = Math.min(
                                                text.length,
                                                matchIndex +
                                                    findText.length +
                                                    40,
                                            );
                                            const snippet = text.slice(
                                                contextStart,
                                                contextEnd,
                                            );
                                            const prefix =
                                                contextStart > 0 ? "..." : "";
                                            const suffix =
                                                contextEnd < text.length
                                                    ? "..."
                                                    : "";
                                            const parts = snippet.split(
                                                new RegExp(
                                                    `(${findText.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")})`,
                                                    "gi",
                                                ),
                                            );

                                            return (
                                                <div className="px-4 py-2 border-t border-blue-200 bg-blue-50 text-xs text-slate-700 rounded-b-lg">
                                                    <span className="text-slate-400 mr-2">
                                                        Match:
                                                    </span>
                                                    <span className="text-slate-500">
                                                        {prefix}
                                                    </span>
                                                    {parts.map((part, i) => {
                                                        const isMatch =
                                                            part.toLowerCase() ===
                                                            findText.toLowerCase();
                                                        return isMatch ? (
                                                            <mark
                                                                key={i}
                                                                className="bg-gold-300 text-slate-900 rounded px-0.5 font-semibold"
                                                            >
                                                                {part}
                                                            </mark>
                                                        ) : (
                                                            <span key={i}>
                                                                {part}
                                                            </span>
                                                        );
                                                    })}
                                                    <span className="text-slate-500">
                                                        {suffix}
                                                    </span>
                                                </div>
                                            );
                                        })()}
                                </div>
                            );
                        })}
                        {paginatedSegments.length === 0 && (
                            <div className="text-center py-12">
                                <Filter className="h-12 w-12 text-gray-600 mx-auto mb-4" />
                                <h3 className="text-lg font-medium text-gray-900 mb-2">
                                    No segments found
                                </h3>
                                <p className="text-gray-700">
                                    Try adjusting your filter or search
                                    criteria.
                                </p>
                            </div>
                        )}
                    </div>
                </div>

                {showTMPanel && (
                    <div
                        className={`absolute bottom-0 left-0 right-0 bg-white border-t-2 border-gold-400 shadow-lg z-30 transition-all duration-200 ${mtPanelOpen || spellCheckOpen || lyraOpen ? "mr-96" : "mr-0"}`}
                    >
                        <TMHorizontalPanel
                            sourceText={
                                segments[activeSegmentIndex]?.source_text || ""
                            }
                            sourceLang={project.source_language}
                            targetLang={project.target_language}
                            onInsert={(text, source) =>
                                handleSegmentChange(activeSegmentIndex, text, source)
                            }
                        />
                    </div>
                )}

                {mtPanelOpen && (
                    <div className="fixed top-0 right-0 bottom-0 w-96 bg-white border-l border-gray-200 shadow-2xl z-40">
                        <div className="h-full flex flex-col">
                            <div className="flex-shrink-0 px-4 py-3 border-b border-gray-200 bg-blue-50">
                                <div className="flex items-center justify-between">
                                    <h3 className="text-lg font-semibold text-blue-900">
                                        Machine Translation
                                    </h3>
                                    <button
                                        onClick={() => setMtPanelOpen(false)}
                                        className="p-1 hover:bg-blue-100 rounded-md transition-colors"
                                    >
                                        <X className="h-5 w-5 text-blue-600" />
                                    </button>
                                </div>
                                <p className="text-xs text-blue-600 mt-1">
                                    AI-powered translation suggestions
                                </p>
                            </div>
                            <div className="flex-1 overflow-y-auto">
                                <MTSuggestionsPanel
                                    isOpen={mtPanelOpen}
                                    onClose={() => setMtPanelOpen(false)}
                                    sourceText={
                                        currentSegment?.source_text || ""
                                    }
                                    sourceLang={project.source_language}
                                    targetLang={project.target_language}
                                    onInsert={(text, source) =>
                                        handleSegmentChange(
                                            activeSegmentIndex,
                                            text,
                                            source,
                                        )
                                    }
                                    onBatchTranslate={() =>
                                        setShowBatchDialog(true)
                                    }
                                    defaultProvider={batchProvider}
                                />
                            </div>
                        </div>
                    </div>
                )}

                {spellCheckOpen && (
                    <div className="fixed top-0 right-0 bottom-0 w-96 bg-white shadow-lg z-40 border-l border-gray-200">
                        <SpellCheckPanel
                            isOpen={spellCheckOpen}
                            onClose={() => setSpellCheckOpen(false)}
                            text={
                                segments[activeSegmentIndex]?.target_text || ""
                            }
                            language={project.target_language}
                            segmentId={segments[activeSegmentIndex]?.id}
                            onApplySuggestion={(originalText, newText) =>
                                handleSegmentChange(activeSegmentIndex, newText)
                            }
                        />
                    </div>
                )}
            </div>

            {/* Status bar */}
            <div
                className={`flex-shrink-0 bg-white border-t border-gray-200 px-6 py-2 ${mtPanelOpen || spellCheckOpen || lyraOpen ? "mr-96" : "mr-0"}`}
            >
                <div className="flex justify-between items-center text-xs text-gray-600">
                    {/* Left side */}
                    <div className="flex items-center gap-4">
                        <span>
                            Segment {activeSegmentIndex + 1} of{" "}
                            {segments.length}
                        </span>
                        <span className="text-gray-300">·</span>
                        <span>
                            {(project.word_count ?? 0).toLocaleString()} words
                        </span>
                        <span className="text-gray-300">·</span>
                        <span>
                            Layout:{" "}
                            {layoutMode === "parallel" ? "Parallel" : "Stacked"}
                        </span>
                        {autoSaveStatus === "saving" && (
                            <div className="flex items-center gap-1">
                                <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-gold-600"></div>
                                <span>Saving...</span>
                            </div>
                        )}
                    </div>
                    {/* Right side — pagination */}
                    {totalPages > 1 && (
                        <div className="flex items-center gap-1">
                            <span className="text-gray-500 mr-2">
                                Showing {startIndex + 1}–
                                {Math.min(endIndex, filteredSegments.length)} of{" "}
                                {filteredSegments.length}
                            </span>
                            <button
                                onClick={() =>
                                    setCurrentPage((p) => Math.max(1, p - 1))
                                }
                                disabled={currentPage === 1}
                                className="px-2 py-1 text-xs rounded border border-gray-300 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                            >
                                ←
                            </button>
                            {Array.from(
                                { length: Math.min(5, totalPages) },
                                (_, i) => {
                                    const page =
                                        totalPages <= 5
                                            ? i + 1
                                            : currentPage <= 3
                                              ? i + 1
                                              : currentPage >= totalPages - 2
                                                ? totalPages - 4 + i
                                                : currentPage - 2 + i;
                                    return (
                                        <button
                                            key={page}
                                            onClick={() => setCurrentPage(page)}
                                            className={`px-2 py-1 text-xs rounded border ${currentPage === page ? "bg-gold-500 text-white border-gold-500" : "border-gray-300 hover:bg-gray-50"}`}
                                        >
                                            {page}
                                        </button>
                                    );
                                },
                            )}
                            <button
                                onClick={() =>
                                    setCurrentPage((p) =>
                                        Math.min(totalPages, p + 1),
                                    )
                                }
                                disabled={currentPage === totalPages}
                                className="px-2 py-1 text-xs rounded border border-gray-300 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                            >
                                →
                            </button>
                            <span className="text-gray-500 ml-1">
                                Page {currentPage}/{totalPages}
                            </span>
                        </div>
                    )}
                </div>
            </div>

            <GoToSegmentDialog
                isOpen={showGoToDialog}
                onClose={() => setShowGoToDialog(false)}
                onGoToSegment={(segmentNumber: number) =>
                    goToSegmentByNumber(segmentNumber)
                }
                totalSegments={segments.length}
                currentSegment={activeSegmentIndex + 1}
            />
            <KeyboardShortcutsDialog
                isOpen={showShortcutsDialog}
                onClose={() => setShowShortcutsDialog(false)}
            />
            {showPreviewPanel && (
                <PreviewPanel
                    segments={segments.map((s) => ({
                        id: s.id,
                        segment_order: s.segment_order,
                        source_text: s.source_text,
                        target_text: s.target_text,
                        status: s.status,
                        page_number: (s as any).page_number ?? null,
                    }))}
                    onSegmentClick={(segmentId) => {
                        const idx = segments.findIndex(
                            (s) => s.id === segmentId,
                        );
                        if (idx !== -1) setActiveSegmentIndex(idx);
                    }}
                    onClose={() => setShowPreviewPanel(false)}
                    fileUrl={project?.file_url ?? null}
                    targetLanguage={project?.target_language}
                />
            )}
            <QAIssuesPanel
                isOpen={showQAPanel}
                onClose={() => setShowQAPanel(false)}
                issues={qaResult?.issues || []}
                segmentNumber={activeSegmentIndex + 1}
            />
            <BatchTranslateDialog
                isOpen={showBatchDialog}
                onClose={() => setShowBatchDialog(false)}
                onConfirm={handleBatchConfirm}
                segments={segments.map((s) => ({
                    id: s.id,
                    source_text: s.source_text,
                    target_text: s.target_text,
                    segment_order: s.segment_order,
                }))}
                defaultProvider={batchProvider}
            />
            <BatchTranslateProgress
                isOpen={showBatchProgress}
                progress={batchProgress}
                result={batchResult}
                provider={batchProvider}
                onCancel={() => batchAbortRef.current?.abort()}
                onDone={() => {
                    setShowBatchProgress(false);
                    setBatchProgress(null);
                    setBatchResult(null);
                }}
                onAIReview={handleBatchAIReview}
            />

            {project && (
                <TranslyxAIPanel
                    isOpen={false}
                    onClose={() => {}}
                    sourceText={segments[activeSegmentIndex]?.source_text || ""}
                    targetText={segments[activeSegmentIndex]?.target_text || ""}
                    sourceLang={project.source_language}
                    targetLang={project.target_language}
                    projectName={project.name}
                    projectId={project.id}
                    allSegments={segments.map((s) => ({
                        id: s.id,
                        segment_order: s.segment_order,
                        source_text: s.source_text,
                        target_text: s.target_text || "",
                    }))}
                    messages={aiMessages}
                    setMessages={setAiMessages}
                    onApplySegmentEdit={(order, newText) => {
                        const seg = segments.find(
                            (s) => s.segment_order === Number(order),
                        );
                        if (!seg) return;
                        const lyraProv = {
                            target_origin: "lyra",
                            origin_score: null,
                            origin_text: newText,
                        };
                        setSegments((prev) =>
                            prev.map((s) =>
                                s.segment_order === Number(order)
                                    ? { ...s, ...lyraProv, target_text: newText }
                                    : s,
                            ),
                        );
                        cancelPendingSave(seg.id);
                        supabase
                            .from("cat_segments")
                            .update({
                                target_text: newText,
                                updated_at: new Date().toISOString(),
                                ...lyraProv,
                            })
                            .eq("id", seg.id)
                            .then(({ error }) => {
                                if (error)
                                    console.error("Lyra apply not saved:", error.message);
                            });
                        lastSavedRef.current[seg.id] = newText;
                    }}
                    onApplyComplete={() => {
                        segments.forEach((s) => {
                            lastSavedRef.current[s.id] = s.target_text ?? "";
                        });
                        setHasUnsavedChanges(false);
                    }}
                />
            )}

            {batchAIReviewing && (
                <div
                    style={{
                        position: "fixed",
                        bottom: 80,
                        left: "50%",
                        transform: "translateX(-50%)",
                        background: "#0f172a",
                        borderRadius: 12,
                        padding: "12px 20px",
                        display: "flex",
                        alignItems: "center",
                        gap: 14,
                        zIndex: 60,
                        minWidth: 320,
                        boxShadow: "0 4px 24px rgba(0,0,0,0.4)",
                    }}
                >
                    <svg
                        width="20"
                        height="20"
                        viewBox="0 0 20 20"
                        style={{ flexShrink: 0 }}
                    >
                        <circle
                            cx="10"
                            cy="10"
                            r="8"
                            fill="none"
                            stroke="#334155"
                            strokeWidth="2"
                        />
                        <circle
                            cx="10"
                            cy="10"
                            r="8"
                            fill="none"
                            stroke="#eab308"
                            strokeWidth="2"
                            strokeDasharray={`${(batchAIReviewProgress / 100) * 50.3} ${50.3 - (batchAIReviewProgress / 100) * 50.3}`}
                            strokeLinecap="round"
                            transform="rotate(-90 10 10)"
                            style={{ transition: "stroke-dasharray 0.3s ease" }}
                        />
                    </svg>
                    <div style={{ flex: 1 }}>
                        <div
                            style={{
                                fontSize: 13,
                                fontWeight: 500,
                                color: "#f1f5f9",
                                marginBottom: 6,
                            }}
                        >
                            TranslyxAI reviewing batch translations…
                        </div>
                        <div
                            style={{
                                width: "100%",
                                height: 3,
                                background: "#334155",
                                borderRadius: 2,
                                overflow: "hidden",
                            }}
                        >
                            <div
                                style={{
                                    width: `${batchAIReviewProgress}%`,
                                    height: "100%",
                                    background: "#eab308",
                                    borderRadius: 2,
                                    transition: "width 0.3s ease",
                                }}
                            />
                        </div>
                    </div>
                    <span
                        style={{
                            fontSize: 12,
                            fontWeight: 600,
                            color: "#eab308",
                            flexShrink: 0,
                        }}
                    >
                        {Math.round(
                            (batchAIReviewProgress / 100) * batchAIReviewTotal,
                        )}{" "}
                        / {batchAIReviewTotal}
                    </span>
                </div>
            )}

            {showAIReviewModal && (
                <div
                    style={{
                        position: "fixed",
                        bottom: 80,
                        right: 16,
                        width: 420,
                        maxHeight: "60vh",
                        zIndex: 60,
                        display: "flex",
                        flexDirection: "column",
                        borderRadius: 14,
                        overflow: "hidden",
                        border: "0.5px solid #e2e8f0",
                        boxShadow: "0 8px 32px rgba(0,0,0,0.18)",
                    }}
                >
                    <div
                        style={{
                            background: "#fff",
                            borderRadius: 14,
                            width: "100%",
                            display: "flex",
                            flexDirection: "column",
                            overflow: "hidden",
                        }}
                    >
                        <div
                            style={{
                                padding: "16px 20px",
                                borderBottom: "0.5px solid #e5e7eb",
                                display: "flex",
                                alignItems: "center",
                                gap: 12,
                            }}
                        >
                            <div
                                style={{
                                    width: 36,
                                    height: 36,
                                    background:
                                        batchAIReviewResults.length > 0
                                            ? "#fef3c7"
                                            : "#dcfce7",
                                    borderRadius: 8,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    flexShrink: 0,
                                }}
                            >
                                <Sparkles
                                    style={{
                                        width: 18,
                                        height: 18,
                                        color:
                                            batchAIReviewResults.length > 0
                                                ? "#d97706"
                                                : "#16a34a",
                                    }}
                                />
                            </div>
                            <div style={{ flex: 1 }}>
                                <div
                                    style={{
                                        fontSize: 15,
                                        fontWeight: 600,
                                        color: "#0f172a",
                                    }}
                                >
                                    AI Review Complete
                                </div>
                                <div
                                    style={{
                                        fontSize: 12,
                                        color: "#64748b",
                                        marginTop: 1,
                                    }}
                                >
                                    {batchAIReviewTotal} segments reviewed ·{" "}
                                    {batchAIReviewResults.length} flagged
                                </div>
                            </div>
                            <button
                                onClick={() => {
                                    setShowAIReviewModal(false);
                                    setBatchAIReviewProgress(0);
                                }}
                                style={{
                                    width: 28,
                                    height: 28,
                                    borderRadius: 6,
                                    border: "0.5px solid #e2e8f0",
                                    background: "#f8fafc",
                                    cursor: "pointer",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    fontSize: 16,
                                    color: "#64748b",
                                    flexShrink: 0,
                                    marginLeft: 4,
                                }}
                            >
                                ×
                            </button>
                            <div style={{ display: "flex", gap: 8 }}>
                                {batchAIReviewResults.length > 0 && (
                                    <span
                                        style={{
                                            padding: "3px 10px",
                                            background: "#fef3c7",
                                            color: "#92400e",
                                            border: "0.5px solid #fcd34d",
                                            borderRadius: 20,
                                            fontSize: 12,
                                            fontWeight: 500,
                                        }}
                                    >
                                        {batchAIReviewResults.length} issue
                                        {batchAIReviewResults.length !== 1
                                            ? "s"
                                            : ""}
                                    </span>
                                )}
                                <span
                                    style={{
                                        padding: "3px 10px",
                                        background: "#dcfce7",
                                        color: "#166534",
                                        border: "0.5px solid #86efac",
                                        borderRadius: 20,
                                        fontSize: 12,
                                        fontWeight: 500,
                                    }}
                                >
                                    {batchAIReviewTotal -
                                        batchAIReviewResults.length}{" "}
                                    passed
                                </span>
                            </div>
                        </div>
                        <div
                            style={{
                                flex: 1,
                                overflowY: "auto",
                                padding: "0 20px",
                            }}
                        >
                            {batchAIReviewResults.length === 0 ? (
                                <div
                                    style={{
                                        padding: "32px 0",
                                        textAlign: "center",
                                    }}
                                >
                                    <div
                                        style={{
                                            fontSize: 32,
                                            marginBottom: 8,
                                        }}
                                    >
                                        ✓
                                    </div>
                                    <div
                                        style={{
                                            fontSize: 15,
                                            fontWeight: 500,
                                            color: "#0f172a",
                                        }}
                                    >
                                        No issues found
                                    </div>
                                    <div
                                        style={{
                                            fontSize: 13,
                                            color: "#64748b",
                                            marginTop: 4,
                                        }}
                                    >
                                        All {batchAIReviewTotal} translations
                                        look good.
                                    </div>
                                </div>
                            ) : (
                                batchAIReviewResults.map((item, i) => (
                                    <div
                                        key={i}
                                        style={{
                                            display: "flex",
                                            alignItems: "flex-start",
                                            gap: 12,
                                            padding: "12px 0",
                                            borderBottom:
                                                i <
                                                batchAIReviewResults.length - 1
                                                    ? "0.5px solid #f1f5f9"
                                                    : "none",
                                        }}
                                    >
                                        <div
                                            style={{
                                                width: 32,
                                                height: 32,
                                                background: "#fef3c7",
                                                borderRadius: 8,
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                fontSize: 13,
                                                fontWeight: 600,
                                                color: "#92400e",
                                                flexShrink: 0,
                                            }}
                                        >
                                            {item.order}
                                        </div>
                                        <div style={{ flex: 1, minWidth: 0 }}>
                                            <div
                                                style={{
                                                    fontSize: 12,
                                                    fontWeight: 500,
                                                    color: "#64748b",
                                                    marginBottom: 2,
                                                }}
                                            >
                                                Segment {item.order}
                                            </div>
                                            <div
                                                style={{
                                                    fontSize: 13,
                                                    color: "#1e293b",
                                                    lineHeight: 1.5,
                                                }}
                                            >
                                                {item.issue}
                                            </div>
                                        </div>
                                        <button
                                            onClick={() => {
                                                setTimeout(() => {
                                                    const idx =
                                                        segmentsRef.current.findIndex(
                                                            (s) =>
                                                                Number(
                                                                    s.segment_order,
                                                                ) ===
                                                                Number(
                                                                    item.order,
                                                                ),
                                                        );
                                                    if (idx === -1) return;
                                                    isProgrammaticNav.current = true;
                                                    setFilterMode("all");
                                                    setCurrentPage(
                                                        Math.floor(
                                                            idx / itemsPerPage,
                                                        ) + 1,
                                                    );
                                                    setActiveSegmentIndex(idx);
                                                    setTimeout(() => {
                                                        const segmentEl =
                                                            document.querySelector(
                                                                `[data-segment-index="${idx}"]`,
                                                            );
                                                        if (segmentEl)
                                                            segmentEl.scrollIntoView(
                                                                {
                                                                    behavior:
                                                                        "smooth",
                                                                    block: "center",
                                                                },
                                                            );
                                                    }, 200);
                                                }, 100);
                                            }}
                                            style={{
                                                padding: "5px 12px",
                                                borderRadius: 7,
                                                border: "0.5px solid #e2e8f0",
                                                fontSize: 12,
                                                fontWeight: 500,
                                                cursor: "pointer",
                                                background: "#f8fafc",
                                                color: "#0f172a",
                                                whiteSpace: "nowrap",
                                                flexShrink: 0,
                                            }}
                                        >
                                            Go to segment
                                        </button>
                                    </div>
                                ))
                            )}
                        </div>
                        <div
                            style={{
                                padding: "12px 20px",
                                background: "#f8fafc",
                                borderTop: "0.5px solid #e5e7eb",
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                            }}
                        >
                            <span style={{ fontSize: 12, color: "#94a3b8" }}>
                                {batchAIReviewResults.length > 0
                                    ? "Issues saved to segment notes"
                                    : "All translations reviewed successfully"}
                            </span>
                            <button
                                onClick={() => {
                                    setShowAIReviewModal(false);
                                    setBatchAIReviewProgress(0);
                                }}
                                style={{
                                    padding: "7px 20px",
                                    background: "#0f172a",
                                    color: "#fff",
                                    border: "none",
                                    borderRadius: 8,
                                    fontSize: 13,
                                    fontWeight: 500,
                                    cursor: "pointer",
                                }}
                            >
                                Done
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {!isLinguaproz && exportDialogOpen && project && (
                <ExportDialog
                    isOpen={exportDialogOpen}
                    onClose={() => setExportDialogOpen(false)}
                    project={project}
                    segments={segments}
                    availableFiles={(() => {
                        const files = [
                            ...new Set(
                                segments
                                    .map((s) => (s as any).source_file)
                                    .filter((f) => f && f !== "default"),
                            ),
                        ];
                        return files.length > 1 ? files : undefined;
                    })()}
                    initialFileScope={selectedFileFilter}
                    fileUrls={
                        ((project as any).file_urls as Record<
                            string,
                            string
                        >) ?? {}
                    }
                />
            )}

            {showHelpPanel && (
                <div
                    className="fixed inset-y-0 right-0 z-50 flex"
                    style={{ pointerEvents: "auto" }}
                >
                    <div
                        className="flex-1"
                        onClick={() => setShowHelpPanel(false)}
                    />
                    <div
                        className="w-96 bg-slate-900 border-l border-slate-700 flex flex-col shadow-2xl"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="px-4 py-3 border-b border-slate-700 shrink-0 flex items-center justify-between">
                            <span className="text-white font-semibold text-sm">
                                Help & Guide
                            </span>
                            <button
                                onClick={() => setShowHelpPanel(false)}
                                className="p-1 rounded hover:bg-slate-700 text-slate-400 hover:text-white transition-colors"
                            >
                                <X className="h-4 w-4" />
                            </button>
                        </div>
                        <div className="px-4 py-2 shrink-0">
                            <input
                                type="text"
                                placeholder="Search help…"
                                value={helpSearch}
                                onChange={(e) => setHelpSearch(e.target.value)}
                                className="w-full px-3 py-1.5 rounded bg-slate-800 border border-slate-600 text-sm text-white placeholder-slate-500 focus:outline-none focus:ring-1 focus:ring-gold-500"
                            />
                        </div>
                        <div className="flex-1 overflow-y-auto px-4 py-2 space-y-2 text-sm text-slate-300">
                            {[
                                {
                                    id: "editor",
                                    title: "Editor Basics",
                                    items: [
                                        "Click a segment to select it and start typing in the Translation field.",
                                        "Press Ctrl+Enter to confirm a segment and move to the next unfinished one.",
                                        "Use Ctrl+↑ / Ctrl+↓ to navigate between segments.",
                                        "Ctrl+G opens the Go To Segment dialog.",
                                    ],
                                },
                                {
                                    id: "tools",
                                    title: "Tools",
                                    items: [
                                        "TM (Translation Memory) — reuse past translations. Ctrl+M toggles the panel.",
                                        "MT — machine translation via DeepL, Google, or MyMemory. Ctrl+T toggles.",
                                        "Batch MT — translate all empty segments at once via the MT panel.",
                                        "AI QA — review batch translations for quality issues.",
                                        "Spell Check — checks target language spelling. Ctrl+Shift+K.",
                                        "AI Assistant — ask questions or get rewrites in context.",
                                    ],
                                },
                                {
                                    id: "shortcuts",
                                    title: "Keyboard Shortcuts",
                                    items: [
                                        "Ctrl+Enter — Confirm segment & next",
                                        "Ctrl+↑ / Ctrl+↓ — Previous / Next segment",
                                        "Ctrl+Shift+S — Copy source to target",
                                        "Ctrl+T — Toggle MT panel",
                                        "Ctrl+M — Toggle TM panel",
                                        "Ctrl+Q — Run QA check",
                                        "Ctrl+G — Go to segment",
                                        "Ctrl+Shift+K — Spell check",
                                        "F1 — All keyboard shortcuts",
                                    ],
                                },
                                {
                                    id: "export",
                                    title: "Exporting",
                                    items: [
                                        "Click Export in the top bar to download your project.",
                                        "Supported formats: XLIFF, DOCX, PDF, TXT, TMX.",
                                        "Confirmed (green) segments are included in all exports.",
                                    ],
                                },
                            ]
                                .filter(
                                    (section) =>
                                        !helpSearch ||
                                        section.title
                                            .toLowerCase()
                                            .includes(
                                                helpSearch.toLowerCase(),
                                            ) ||
                                        section.items.some((i) =>
                                            i
                                                .toLowerCase()
                                                .includes(
                                                    helpSearch.toLowerCase(),
                                                ),
                                        ),
                                )
                                .map((section) => (
                                    <div
                                        key={section.id}
                                        className="border border-slate-700 rounded-lg overflow-hidden"
                                    >
                                        <button
                                            className="w-full flex items-center justify-between px-3 py-2 bg-slate-800 hover:bg-slate-700 text-left transition-colors"
                                            onClick={() =>
                                                setOpenHelpSection(
                                                    openHelpSection ===
                                                        section.id
                                                        ? null
                                                        : section.id,
                                                )
                                            }
                                        >
                                            <span className="font-medium text-white text-xs">
                                                {section.title}
                                            </span>
                                            <ChevronDown
                                                className={`h-3.5 w-3.5 text-slate-400 transition-transform ${openHelpSection === section.id ? "rotate-180" : ""}`}
                                            />
                                        </button>
                                        {openHelpSection === section.id && (
                                            <ul className="px-3 py-2 space-y-1.5 bg-slate-900">
                                                {section.items
                                                    .filter(
                                                        (item) =>
                                                            !helpSearch ||
                                                            item
                                                                .toLowerCase()
                                                                .includes(
                                                                    helpSearch.toLowerCase(),
                                                                ),
                                                    )
                                                    .map((item, i) => (
                                                        <li
                                                            key={i}
                                                            className="flex items-start gap-2 text-xs text-slate-300"
                                                        >
                                                            <span className="text-gold-500 mt-0.5 shrink-0">
                                                                ·
                                                            </span>
                                                            <span>{item}</span>
                                                        </li>
                                                    ))}
                                            </ul>
                                        )}
                                    </div>
                                ))}
                        </div>
                        <div className="px-4 py-3 border-t border-slate-700 shrink-0">
                            <a
                                href="https://translyx.app/guide"
                                target="_blank"
                                rel="noreferrer"
                                className="flex items-center justify-center gap-2 w-full py-2 bg-slate-800 hover:bg-slate-700 border border-slate-600 rounded-lg text-xs text-slate-300 hover:text-white transition-colors"
                            >
                                Full guide at translyx.app/guide
                            </a>
                        </div>
                    </div>
                </div>
            )}

            <UpgradeModal
                isOpen={showUpgradeModal}
                onClose={() => setShowUpgradeModal(false)}
                limitType={upgradeLimitType}
                currentTier={userTier}
            />
        </div>
    );
};

export default EditorPage;
